import axios from 'axios'
import { auth } from '../firebase'
import clientSettings from './clientSettings'
import tools from './tools'
import { getApiErrorMessage, normalizeApiError } from './apiError'
import { isGenericStockProject } from '@/constants/genericStockProjects'

const API = axios.create({
    baseURL: process.env.VUE_APP_API_HOST,
})

const WORK_ORDER_PAGE_SIZE = 50

const inflightLookups = new Map()

function shareLookup(key, loader) {
    const existing = inflightLookups.get(key)
    if (existing) {
        return existing
    }
    const promise = Promise.resolve()
        .then(loader)
        .finally(() => {
            if (inflightLookups.get(key) === promise) {
                inflightLookups.delete(key)
            }
        })
    inflightLookups.set(key, promise)
    return promise
}

async function collectWorkOrderPages(loadPage) {
    const workOrders = []
    let offset = 0
    for (;;) {
        const page = await loadPage(WORK_ORDER_PAGE_SIZE, offset)
        if (!Array.isArray(page) || page.length === 0) {
            break
        }
        workOrders.push(...page)
        if (page.length < WORK_ORDER_PAGE_SIZE) {
            break
        }
        offset += page.length
    }
    return workOrders
}

function buildReportErrorMessage(error) {
    return getApiErrorMessage(
        error,
        'Could not reach the server to generate the report (network error, possibly caused by the connection being dropped). Please try again; if the problem persists, try a shorter date range.'
    )
}

API.interceptors.request.use(
    async function(config) {
        try {
            const token = auth().currentUser
                ? await auth().currentUser.getIdToken()
                : ''
            config.headers.common.Authorization = `Bearer ${token}`
            config.headers.common['Access-Control-Allow-Origin'] = '*'
            config.headers.common['Access-Control-Allow-Methods'] =
                'GET, POST, PUT, DELETE'
            config.headers.common['Access-Control-Allow-Headers'] =
                'Content-Type'
            config.headers.common['cache-control'] = 'no-cache'
            config.headers.common['X-Content-Type-Options'] = 'nosniff'
            config.headers.common['X-Frame-Options'] = 'SAMEORIGIN'
            config.headers.common['Cross-Origin-Resource-Policy'] = 'same-site'
            return config
        } catch (error) {
            return Promise.reject(normalizeApiError(error))
        }
    },
    function(error) {
        return Promise.reject(normalizeApiError(error))
    }
)

API.interceptors.response.use(
    function(response) {
        return response
    },
    function(error) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            return Promise.reject(error)
        }
        return Promise.reject(normalizeApiError(error))
    }
)

function costExchangesForApi(costExchanges) {
    if (!costExchanges || !Array.isArray(costExchanges.exchanges)) {
        return costExchanges
    }
    return {
        ...costExchanges,
        exchanges: costExchanges.exchanges.map(exchange => ({
            ...exchange,
            money:
                exchange.money == null || exchange.money === ''
                    ? exchange.money
                    : String(exchange.money),
        })),
    }
}

export default {
    // client settings
    ...clientSettings(API),
    // tool tags
    ...tools(API),
    async getHeaders() {
        try {
            const response = await API.get('/authentication/headers')
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /**
     *
     * USERS ENDPOINTS
     *
     */

    async createUser(user) {
        try {
            const {
                name,
                email,
                phone,
                picture,
                role,
                phone2,
                permissions,
                language,
                process,
                signCode,
                operatorRole,
                documentId,
            } = user
            const payload = {
                name,
                email,
                phone,
                picture,
                phone2,
                language: language,
                role,
                permissions,
                processData: process,
                signCode,
                operatorRole,
            }
            if (documentId) {
                payload.documentId = documentId
            }
            const response = await API.post('/users', payload)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async unsubscribeFromTopic(token) {
        try {
            const response = await API.put('/users/unsubscribeFromTopic', {
                token,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async subscribeToTopic(token) {
        try {
            const response = await API.put('/users/subscribeToTopic', { token })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async logLogin() {
        try {
            const response = await API.post('/users/log-login')
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getUsers() {
        try {
            const response = await API.get('/users')
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getAuthUsers() {
        try {
            const response = await API.get('/users/authUsers')
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getUser(id) {
        try {
            const {
                data: { user },
            } = await API.get(`/users/${id}`)
            return user
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getLiteUsers(options = {}) {
        try {
            const config = {}
            if (options.signal) {
                config.signal = options.signal
            }
            // Add companyId to query params if provided (for anonymous users)
            if (options.companyId) {
                config.params = { companyId: options.companyId }
            }
            const response = await API.get('/users/lite', config)
            return response
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                throw error
            }
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getUsersLite(options = {}) {
        return this.getLiteUsers(options)
    },
    async getAllLiteUsers() {
        try {
            const response = await API.get('/users/lite-all')
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getWoUsers() {
        try {
            const response = await API.get('/users/wo-users')
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async usersChangelog() {
        try {
            const {
                data: { users },
            } = await API.get('/users/changelog')
            return users
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async usersLogs(params) {
        try {
            const { userId, startDate, endDate } = params
            const {
                data: { logs },
            } = await API.get(
                `/users/logs?userId=${userId}&startDate=${startDate}&endDate=${endDate}`
            )
            return logs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createProject(project) {
        try {
            const {
                reference,
                sapCode,
                name,
                accountManager,
                projectManager,
                client,
                quotes,
                currency,
                country,
                state,
                city,
                address,
                collaborators,
                designManagers,
                price,
                offer,
                generic,
                estimatedMargin,
                classification,
                clientContacts,
            } = project
            const response = await API.post('/projects', {
                reference,
                sapCode,
                name,
                accountManager,
                projectManager,
                client,
                quotes,
                currency,
                country,
                state,
                city,
                address,
                collaborators,
                designManagers,
                price,
                offer,
                generic,
                estimatedMargin,
                classification,
                clientContacts,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async projectFromQuote(params) {
        try {
            const {
                reference,
                sapCode,
                name,
                accountManager,
                projectManager,
                client,
                quotes,
                currency,
                country,
                state,
                city,
                address,
                collaborators,
                designManagers,
                price,
                offer,
                quoteId,
                estimatedMargin,
                oc,
                classification,
            } = params
            const response = await API.post('/projects/project-from-quote', {
                reference,
                sapCode,
                name,
                accountManager,
                projectManager,
                client,
                quotes,
                currency,
                country,
                state,
                city,
                address,
                collaborators,
                designManagers,
                price,
                offer,
                quoteId,
                estimatedMargin,
                oc,
                classification,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async awardedInProject(params) {
        try {
            const response = await API.post(
                '/projects/quote-awarded-in-project',
                params
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createDocumentClient(document) {
        try {
            const { clientId, name, file, companyId } = document

            const response = await API.post('/documents/clients', {
                clientId,
                name,
                file,
                companyId,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async adminUploadClientDocument({
        clientId,
        fileType,
        name,
        file,
        expirationDate,
    }) {
        try {
            const {
                data: { client, document },
            } = await API.post('/clients/admin-upload-document', {
                clientId,
                fileType,
                name,
                file,
                expirationDate,
            })
            return { client, document }
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createDocumentUser(document) {
        try {
            const { name, file, userId } = document

            const response = await API.post('/documents/users', {
                name,
                file,
                userId,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getOne(id) {
        try {
            const response = await API.get(`/users/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getMyInfo(options = {}) {
        const load = async () => {
            const config = {}
            if (options.signal) {
                config.signal = options.signal
            }
            return await API.get('/users/myInfo', config)
        }
        try {
            if (options.signal) {
                return await load()
            }
            return await shareLookup('users:myInfo', load)
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                throw error
            }
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getOperators() {
        try {
            const {
                data: { users },
            } = await API.get('/users/operators')
            return users
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updatePermissions(userId, permissions) {
        const user = { permissions }
        try {
            const response = await API.put(`/users/permissions/${userId}`, user)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateContact(id, contact) {
        try {
            const response = await API.put(`/contacts/${id}`, contact)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getClientDocuments(id, companyId) {
        try {
            const response = await API.get(`/documents/clients/${id}`, {
                companyId,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getDocumentsByType(type) {
        try {
            const {
                data: { documents },
            } = await API.get(`/documents/by-type/${type}`)
            return documents
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getUserDocuments(id) {
        try {
            const response = await API.get(`/documents/users/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getClients(params) {
        try {
            const response = await API.get('/clients', { params })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getLiteClients() {
        try {
            const response = await API.get('/clients/lite')
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getClient(params) {
        try {
            const response = await API.get(
                `/clients/client?clientId=${params.clientId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getClientRequests(salesRepId) {
        try {
            const {
                data: { requests },
            } = await API.get(`/clients/${salesRepId}/client-requests`)
            return requests
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async approveClientRequest(salesRepId, requestId) {
        try {
            const {
                data: { request },
            } = await API.post(
                `/clients/${salesRepId}/client-requests/${requestId}/approve`
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async assignClientRequest(salesRepId, requestId, clientId) {
        try {
            const {
                data: { request },
            } = await API.post(
                `/clients/${salesRepId}/client-requests/${requestId}/assign`,
                { clientId }
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async rejectClientRequest(salesRepId, requestId) {
        try {
            const {
                data: { request },
            } = await API.post(
                `/clients/${salesRepId}/client-requests/${requestId}/reject`
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createClient(client) {
        try {
            Object.keys(client).forEach(key => {
                if (!client[key]) {
                    delete client[key]
                }
            })
            const response = await API.post('/clients', client)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteClient(id) {
        try {
            const response = await API.delete(`/clients/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createContact(contact) {
        try {
            const {
                name,
                picture,
                clientId,
                supplierId,
                phone,
                phone2,
                state,
                country,
                city,
                email,
                role,
                note,
            } = contact
            const response = await API.post('/contacts', {
                name,
                picture,
                phone,
                clientId,
                supplierId,
                country,
                city,
                phone2,
                state,
                email,
                role,
                note,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getContacts(params) {
        try {
            const response = await API.get(`/contacts/`, {
                params,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getContactsByClient(params) {
        try {
            const response = await API.get(
                `/contacts/contactsByClient?clientId=${params.clientId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getRoles(params) {
        try {
            const response = await API.get(`/roles/`, {
                params,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteContact(id) {
        try {
            const response = await API.delete(`/contacts/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateClient(id, client) {
        try {
            const response = await API.put(`/clients/${id}`, client)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateUser(id, user) {
        try {
            const response = await API.put(`/users/${id}`, user)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateOwnSignature(payload) {
        try {
            const response = await API.put(`/users/signature`, payload)
            return response
        } catch (error) {
            const errorResponse = error.response?.data
            throw new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
        }
    },
    async updateEngineeringHours(id, user) {
        try {
            const response = await API.put(
                `/users/updateEngineeringHours/${id}`,
                user
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteUser(id) {
        try {
            const response = await API.delete(`/users/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteProject(id) {
        try {
            const response = await API.delete(`/projects/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateMilestonesRowOrder(projectId, costIds) {
        try {
            await API.put(`/projects/${projectId}/milestones-row-order`, {
                costIds,
            })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                (errorResponse && errorResponse.error) || error.message
            )
        }
    },
    async updateScopeGroups(projectId, scopeGroups) {
        try {
            const {
                data: { scopeGroups: savedGroups },
            } = await API.put(`/projects/${projectId}/scope-groups`, {
                scopeGroups,
            })
            return savedGroups
        } catch (error) {
            const errorResponse = error.response.data
            throw new Error(
                (errorResponse && errorResponse.error) || error.message
            )
        }
    },
    async updateProject(id, project) {
        try {
            const {
                reference,
                sapCode,
                name,
                accountManager,
                projectManager,
                client,
                quotes,
                currency,
                country,
                state,
                city,
                address,
                collaborators,
                designManagers,
                price,
                followUp,
                archive,
                offer,
                generic,
                exports,
                clientPortfolio,
                closingInSAP,
                removedFromSharePoint,
                closingMeetingCompleted,
                estimatedMargin,
                classification,
                clientContacts,
            } = project
            const response = await API.put(`/projects/${id}`, {
                reference,
                sapCode,
                name,
                accountManager,
                projectManager,
                client,
                quotes,
                currency,
                country,
                state,
                city,
                address,
                collaborators,
                designManagers,
                price,
                followUp,
                archive,
                offer,
                generic,
                exports,
                clientPortfolio,
                closingInSAP,
                removedFromSharePoint,
                closingMeetingCompleted,
                estimatedMargin,
                classification,
                clientContacts,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updatePuchaseApprover(params) {
        try {
            const response = await API.put(
                '/projects/purchase-approver',
                params
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async returnProjectFromClosing(projectId) {
        try {
            const response = await API.put(
                `/projects/return-project-from-closing?id=${projectId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createQuote(quote) {
        try {
            const {
                companyId,
                userId,
                clientId,
                notes,
                currency,
                exchange,
                files,
                leadId,
                name,
                country,
                state,
                city,
                tags,
                contacts,
                requestDate,
                invitationDate,
                promiseDate,
            } = quote
            const response = await API.post('/quotes', {
                companyId,
                userId,
                clientId,
                currency,
                leadId,
                exchange,
                files,
                status: 'OPEN',
                name,
                notes,
                country,
                state,
                city,
                tags,
                contacts,
                requestDate,
                invitationDate,
                promiseDate,
            })

            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async forBidding(lead) {
        try {
            const {
                companyId,
                leadId,
                promiseDate,
                estimatorId,
                notificationMessage,
                contacts,
            } = lead
            const response = await API.post('/leads/forBidding', {
                companyId,
                status: 'OPEN',
                leadId,
                promiseDate,
                estimatorId,
                notificationMessage,
                contacts,
            })

            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getQuotes({ status, quoteId, clientId, awarded }) {
        try {
            if (quoteId) {
                return await API.get(`/quotes?quoteId=${quoteId}`)
            } else {
                return await API.get(
                    `/quotes${status ? `?status=${status}` : ''}${
                        clientId
                            ? status
                                ? `&clientId=${clientId}`
                                : `?clientId=${clientId}`
                            : ''
                    }${
                        awarded != undefined
                            ? clientId
                                ? `&awarded=${awarded}`
                                : `?awarded=${awarded}`
                            : ''
                    }`
                )
            }
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async setQuoteDates(params) {
        try {
            const { quoteId, invitationDate, requestDate, promiseDate } = params
            const {
                data: { response },
            } = await API.put(`/quotes/setDates/${quoteId}`, {
                invitationDate,
                requestDate,
                promiseDate,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async changeEstimator(params) {
        try {
            const { id, estimatorId } = params
            const {
                data: { response },
            } = await API.put(`/quotes/changeEstimator/${id}`, {
                estimatorId,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async changePreconManager(params) {
        try {
            const { id, preconManagerId } = params
            const {
                data: { response },
            } = await API.put(`/quotes/changePreconManager/${id}`, {
                preconManagerId,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getQuoteLogs(id) {
        try {
            const {
                data: { quotes },
            } = await API.get(`/quotes/logs?id=${id}`)
            return quotes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getLiteQuotes(params) {
        try {
            const {
                data: { quotes },
            } = await API.get('/quotes/lite', { params })
            return quotes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getDraftQuote(params) {
        try {
            const {
                data: { quote },
            } = await API.get('/quotes/draftQuote', { params })
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getSimpleQuotesByIds(params) {
        try {
            const {
                data: { quotes },
            } = await API.post('/quotes/simpleQuotesByIds', { params })
            return quotes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getBasicInfoQuotes() {
        try {
            const {
                data: { quotes },
            } = await API.get('/quotes/import')
            return quotes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getQuotesByCustomizedParameters(paramToSearch) {
        try {
            return await API.get(
                `/quotes/QuotesByCustomizedParameters?paramToSearch=${paramToSearch}`
            )
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getQuote(id) {
        try {
            const response = await API.get(`/quotes/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateQuote(id, quote) {
        try {
            if (quote.costExchanges) {
                quote.costExchanges = costExchangesForApi(quote.costExchanges)
            }
            const keys = [
                'userId',
                'currency',
                'exchange',
                'companyId',
                'clientId',
                'collaborators',
                'clientCollaborators',
                'directedByClientId',
                'salesRepIds',
                'followUpCollaborators',
                'commissions',
                'totalCosts',
                'commercialTerms',
                'contacts',
                'files',
                'notes',
                'name',
                'status',
                'image',
                'costExchanges',
                'country',
                'state',
                'city',
                'tags',
                'notificationId',
                'timelineEvent',
                'followUpDate',
                'changeOrderRequest',
            ]
            Object.keys(quote).forEach(key => {
                if (!keys.includes(key) || quote[key] === undefined) {
                    delete quote[key]
                }
            })
            const response = await API.put(`/quotes/${id}`, quote)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateDraftQuote(id, quote) {
        try {
            const response = await API.put(`/quotes/draftQuote/${id}`, quote)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createCalculatorItem({ quoteId, ...item }) {
        try {
            const {
                data: { item: created },
            } = await API.post('/quotes/calculator/item', {
                quoteId,
                ...item,
            })
            return created
        } catch (error) {
            const errorResponse = error.response.data
            throw new Error(errorResponse.message || errorResponse.error)
        }
    },
    async updateCalculatorItem({ quoteId, itemId, ...patch }) {
        try {
            const body = { quoteId, itemId }
            Object.keys(patch).forEach(key => {
                if (patch[key] !== undefined) body[key] = patch[key]
            })
            const {
                data: { item },
            } = await API.put('/quotes/calculator/item', body)
            return item
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteCalculatorItem({ quoteId, itemId }) {
        try {
            await API.delete('/quotes/calculator/item', {
                params: { quoteId, itemId },
            })
        } catch (error) {
            const errorResponse = error.response.data
            throw new Error(errorResponse.message || errorResponse.error)
        }
    },
    async updateScheduleMessage({ quoteId, scheduleMessage }) {
        try {
            const {
                data: { scheduleMessage: saved },
            } = await API.put('/quotes/calculator/schedule-message', {
                quoteId,
                scheduleMessage,
            })
            return saved
        } catch (error) {
            const errorResponse = error.response.data
            throw new Error(errorResponse.message || errorResponse.error)
        }
    },
    async updateCalculatorItemsBulk({ quoteId, items }) {
        try {
            await API.put('/quotes/calculator/items/bulk', {
                quoteId,
                items,
            })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async replaceCalculatorItems({
        quoteId,
        items,
        model,
        importBatchId,
        offset,
        done,
    }) {
        try {
            const {
                data: { calculator },
            } = await API.put('/quotes/calculator/items/replace', {
                quoteId,
                items,
                model,
                importBatchId,
                offset,
                done,
            })
            return calculator
        } catch (error) {
            const errorResponse = error.response.data
            throw new Error(errorResponse.message || errorResponse.error)
        }
    },
    async modificationRequest({ id, quoteUpdates }) {
        try {
            const keys = [
                'userId',
                'companyId',
                'clientId',
                'contacts',
                'notes',
                'name',
                'country',
                'state',
                'city',
                'tags',
                'estimatorId',
                'preconManagerId',
                'invitationDate',
                'userToEdit',
                'requestDate',
                'promiseDate',
                'files',
                'changeOrderRequest',
                'qtyOrders',
            ]
            Object.keys(quoteUpdates).forEach(key => {
                if (!keys.includes(key) || quoteUpdates[key] === undefined) {
                    delete quoteUpdates[key]
                }
            })
            const quote = await API.put(
                `/quotes/modification-request/${id}`,
                quoteUpdates
            )
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async approveModificationRequest({ id, promiseDate, estimatorId }) {
        try {
            const quote = await API.put(
                `/quotes/approve-modification-request/${id}`,
                { promiseDate, estimatorId }
            )
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async returnToFollowUp({ id }) {
        try {
            const response = await API.put(`/quotes/return-to-followup/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async arrangeQuoteCosts(id, costs) {
        try {
            const response = await API.put(`/quotes/arrange-costs/${id}`, {
                costs,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteUserDocuments(id) {
        try {
            const response = await API.delete(`/documents/users/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getSettings(params) {
        try {
            const key = `settings:${JSON.stringify(params || {})}`
            return await shareLookup(key, () =>
                API.get(`/settings`, { params })
            )
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateSetting(id, setting) {
        const {
            costSections,
            exchanges,
            measureUnits,
            globalCurrency,
            commercialTerms,
            rejectionReasons,
            generalInfo,
            pdfImages,
            roles,
        } = setting
        try {
            const response = await API.put(`/settings/${id}`, {
                costSections,
                exchanges,
                measureUnits,
                globalCurrency,
                commercialTerms,
                rejectionReasons,
                pdfImages,
                generalInfo,
                roles,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getMaterials() {
        try {
            const { data } = await API.get(`/settings/materials/`)
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message ||
                    errorResponse.error ||
                    'Failed to get materials'
            )
        }
    },
    async updateMaterialsSetting(id, { inputDefinitions }) {
        try {
            const response = await API.put(`/settings/materials/${id}`, {
                inputDefinitions,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message ||
                    errorResponse.error ||
                    'Failed to update materials'
            )
        }
    },
    async createMaterial(material) {
        try {
            const response = await API.post('/settings/materials/material', {
                material,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message ||
                    errorResponse.error ||
                    'Failed to create material'
            )
        }
    },
    async updateMaterial(material) {
        try {
            const response = await API.put(
                `/settings/materials/material?id=${material.id}`,
                {
                    material,
                }
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message ||
                    errorResponse.error ||
                    'Failed to update materials'
            )
        }
    },
    async deleteMaterial(materialId) {
        try {
            const response = await API.delete(
                `/settings/materials/material?id=${materialId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message ||
                    errorResponse.error ||
                    'Failed to delete material'
            )
        }
    },
    // Extrusions methods
    async getExtrusions() {
        try {
            const { data } = await API.get(`/settings/extrusions/extrusions`)
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message ||
                    errorResponse.error ||
                    'Failed to get extrusions'
            )
        }
    },
    async saveExtrusions(extrusions) {
        try {
            const response = await API.post('/settings/extrusions/extrusions', {
                extrusions,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message ||
                    errorResponse.error ||
                    'Failed to save extrusions'
            )
        }
    },
    //SETTINGS: Update section Quotes(rejectedReasos)
    async createReason(params) {
        try {
            const response = await API.post('/settings/lost-reasons', params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateReason(params) {
        try {
            const response = await API.put('/settings/lost-reasons', params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteReason({ settingId, id }) {
        try {
            const response = await API.delete(
                `/settings/lost-reasons?settingId=${settingId}&id=${id}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getTools(params) {
        try {
            const response = await API.get(`/tools`, { params })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getCommercialImages() {
        try {
            const response = await API.get('/tools/commercialImages')
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateTools(id, tool) {
        try {
            const {
                commercialImages,
                commercialDocuments,
                technicalDocuments,
            } = tool
            const response = await API.put(`/tools/updateTools?id=${id}`, {
                commercialImages,
                commercialDocuments,
                technicalDocuments,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateCommercialImage(id, params) {
        try {
            await API.put(`/tools/updateCommercialImage?id=${id}`, params)
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async newCommercialImages(params) {
        try {
            const response = await API.post(
                `/tools/newCommercialImages`,
                params
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteCommercialImage(id) {
        try {
            await API.delete(`/tools/deleteCommercialImage?id=${id}`)
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getPreviewPDF({ quoteId, language }) {
        try {
            const {
                data: { filename },
            } = await API.get('/send/previewPDF', {
                params: {
                    quoteId,
                    language,
                },
            })
            return filename
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendEmail(email) {
        try {
            const {
                userId,
                subject,
                to,
                text,
                cc,
                quoteId,
                attach,
                emailBody,
                notificationId,
            } = email
            const response = await API.post('/send', {
                userId,
                subject,
                to,
                text,
                cc,
                quoteId,
                attach,
                emailBody,
                notificationId,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteQuote(id) {
        try {
            const response = await API.delete(`/quotes/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteCosts(quoteId, costId) {
        try {
            const response = await API.delete(
                `/cost?quoteId=${quoteId}&costId=${costId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteDocUser(userId, id) {
        try {
            const response = await API.delete(`/documents/users/${id}`, {
                userId: userId,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteDocClient(id) {
        try {
            const response = await API.delete(`/documents/clients/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getCosts(id) {
        try {
            const {
                data: { costs },
            } = await API.get(`/cost/all?quoteId=${id}`)
            return costs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getCost(params) {
        try {
            const { quoteId, costId } = params
            const {
                data: { cost },
            } = await API.get(`/cost?quoteId=${quoteId}&costId=${costId}`)
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createFile(id) {
        try {
            return await API.post(`/quotes/createFile`, { id })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createCosts(idQuote, item) {
        try {
            const {
                data: { cost },
            } = await API.post(`cost`, { quoteId: idQuote, ...item })
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createScope(item) {
        try {
            const {
                data: { scope },
            } = await API.post(`scope`, { ...item })
            return scope
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getScopes(userId) {
        try {
            const {
                data: { scopes },
            } = await API.get(`/scope/all?userId=${userId}`)
            return scopes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getAllCompanyScopes() {
        try {
            const {
                data: { scopes },
            } = await API.get(`/scope/all?allCompany=true`)
            return scopes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateScope(item) {
        try {
            delete item.updatedOn
            delete item.createdOn
            delete item.lastDate
            const {
                data: { scope },
            } = await API.put(`scope`, {
                ...item,
            })
            return scope
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteScope(userId, scopeId) {
        try {
            const response = await API.delete(
                `/scope?scopeId=${scopeId}&userId=${userId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async importScopes(params) {
        try {
            const {
                data: { costs },
            } = await API.post(`scope/import`, params)
            return costs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async exportScopes(params) {
        try {
            const {
                data: { scopes },
            } = await API.post(`scope/export`, params)
            return scopes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createImportedCosts(idQuote, item) {
        try {
            const {
                data: { cost },
            } = await API.post(`cost/imported`, { quoteId: idQuote, ...item })
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async importCosts(params) {
        try {
            const {
                data: { costs, alternates },
            } = await API.post(`cost/import`, params)
            return { costs, alternates }
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateCosts(quoteId, item) {
        try {
            delete item.createdOn
            delete item.createdBy
            delete item.updatedOn
            delete item.documentId
            delete item.onHoldReasons
            delete item.engIngressBy
            delete item.engineering
            delete item.engineeringDate
            delete item.engineer
            delete item.engExitBy
            delete item.engineeringIngressDate
            delete item.engineeringExitDate
            delete item.preDesign
            delete item.syncedExportTariff
            delete item.syncedTotalCosts

            const {
                data: { cost },
            } = await API.put(`cost`, {
                ...item,
                quoteId,
            })
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateAssociateInstallation(quoteId, costId, item) {
        try {
            const {
                data: { cost },
            } = await API.put(`cost/associate-installation`, {
                ...item,
                quoteId,
                costId,
            })
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async setInstallationCost(params) {
        try {
            const {
                data: { cost },
            } = await API.put(`cost/setInstallationCost`, params)
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateHistoryCosts(idQuote, cost) {
        const {
            costs,
            totalCosts,
            file,
            currency,
            files,
            exchange,
            costExchanges,
        } = cost
        try {
            const response = await API.put(`quotes/historyCosts/${idQuote}`, {
                costs,
                file,
                totalCosts,
                currency,
                files,
                exchange,
                costExchanges: costExchangesForApi(costExchanges),
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getHistoryCosts(id) {
        try {
            const response = await API.get(`/quotes/historyCosts/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getHistoryQuotes(clientId) {
        try {
            const response = await API.get(
                `/history${clientId ? `?clientId=${clientId}` : ''}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteQuoteHistory(id) {
        try {
            const response = await API.delete(`/history/deleteQuote/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async restartQuote(id, notificationId) {
        try {
            const response = await API.post(`/history/restartQuote`, {
                id,
                notificationId,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createQuoteHistory(id, status = null, rejectReason, notificationId) {
        try {
            const params = {
                id,
                rejectReason,
                notificationId,
            }
            if (status) {
                params.status = status
            }
            const response = await API.post(`/history/`, params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async markQuoteAsLost(id, status = null, rejectReason, notificationId) {
        try {
            const params = {
                id,
                rejectReason,
                notificationId,
            }
            if (status) {
                params.status = status
            }
            const response = await API.post(`/history/markAsLost`, params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async markQuoteAsAwarded(id, status = null, notificationId) {
        try {
            const params = {
                id,
                notificationId,
            }
            if (status) {
                params.status = status
            }
            const response = await API.post(`/history/markAsAwarded`, params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getArchivedVersions(id) {
        try {
            const response = await API.get(`/history/historyCosts/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getArchivedCosts(id) {
        try {
            const response = await API.get(`/history/costs/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getPdfImages(id) {
        try {
            const response = await API.get(`/settings/pdfImages/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getCommercialTerms(id) {
        try {
            const response = await API.get(`/settings/commercialTerms/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getCommercialTermsClients(id) {
        try {
            const response = await API.get(
                `/settings/commercialTermsClients/${id}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateImages(idSetting, pdfImages) {
        const { id, images } = pdfImages
        try {
            const response = await API.put(`/settings/pdfImages/${idSetting}`, {
                images,
                id,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateTerms(idSetting, commercialTerms) {
        const { id, terms } = commercialTerms
        try {
            const response = await API.put(
                `/settings/commercialTerms/${idSetting}`,
                {
                    terms,
                    id,
                }
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateTermsClients(idSetting, commercialTerms) {
        const { id, terms } = commercialTerms
        try {
            const response = await API.put(
                `/settings/commercialTermsClients/${idSetting}`,
                {
                    terms,
                    id,
                }
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateTermsClientsHPL(idSetting, commercialTerms) {
        const { id, terms, leadTimeText } = commercialTerms
        try {
            const response = await API.put(
                `/settings/commercialTermsClients/${idSetting}`,
                {
                    terms,
                    id,
                    leadTimeText,
                }
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getSimpleQuotes(startDate, endDate) {
        try {
            const response = await API.get(
                `/quotes/simpleQuotes?startDate=${startDate}&endDate=${endDate}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getNewCompleteQuotes(startDate, endDate) {
        try {
            const response = await API.get(
                `/quotes/newCompleteQuotes?startDate=${startDate}&endDate=${endDate}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getCompleteQuotes(startDate, endDate) {
        try {
            const response = await API.get(
                `/quotes/completeQuotes?startDate=${startDate}&endDate=${endDate}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createAlternativeCosts(quoteId, params) {
        try {
            const {
                data: { alternate },
            } = await API.post(`/cost/alternate`, {
                quoteId,
                ...params,
            })
            return alternate
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getSentQuotes(startDate, endDate) {
        try {
            const response = await API.get(
                `/quotes/getSentQuotes?startDate=${startDate}&endDate=${endDate}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async changeMainContact(id, params) {
        try {
            const response = await API.put(
                `/quotes/changeMainContact?id=${id}`,
                params
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getEmailBody(id) {
        try {
            const response = await API.get(`/settings/emailBody?id=${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateEmailBody(idSetting, emailBody) {
        const { id, body } = emailBody
        try {
            const response = await API.put(
                `/settings/emailBody?id=${idSetting}`,
                {
                    body,
                    id,
                }
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    // FOLLOW UP POSTS
    async createPost(params) {
        try {
            const {
                data: { post },
            } = await API.post(`/chat/post`, params)
            return post
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getPosts(chatId) {
        try {
            const {
                data: { posts },
            } = await API.get(`/chat/post?chatId=${chatId}`)
            return posts
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createEvent(params) {
        try {
            const {
                data: { event },
            } = await API.post(`/chat/event`, params)
            return event
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getEvent(eventId) {
        try {
            const {
                data: { event },
            } = await API.get(`/chat/event?eventId=${eventId}`)
            return event
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async projections(params) {
        try {
            Object.keys(params).forEach(key => {
                if (!params[key]) {
                    delete params[key]
                }
            })
            const {
                data: { projection },
            } = await API.post(`/chat/projections`, params)
            return projection
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProjections(params) {
        try {
            const { id, startDate, endDate } = params
            const {
                data: { projections },
            } = await API.get(
                `/chat/projections${
                    id
                        ? `?id=${id}`
                        : `?startDate=${startDate}&endDate=${endDate}`
                }`
            )
            return projections
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getAllProjections() {
        try {
            const {
                data: { projections },
            } = await API.get(`/chat/allProjections`)
            return projections
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteEvent(eventId, chatId, postId) {
        try {
            const {
                data: { response },
            } = await API.delete(
                `/chat/event?chatId=${chatId}&eventId=${eventId}&postId=${postId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getChatInfo(id) {
        try {
            const {
                data: { chats },
            } = await API.get(`/chat/?chatId=${id}`)
            if (chats) {
                return chats[0]
            } else {
                throw new Error(`Chat not found`)
            }
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateChat(params) {
        try {
            const {
                data: { update },
            } = await API.put(`/chat/`, params)
            return update
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /**
     * Companies
     */
    async getCompanyGeneralInfo(companyId) {
        try {
            const {
                data: { company },
            } = await API.get(`company/general-info?companyId=${companyId}`)
            return company
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateCompany(companyId, params) {
        try {
            const {
                data: { company },
            } = await API.put(
                `company/general-info?companyId=${companyId}`,
                params
            )
            return company
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /**
     * Calculations
     */
    async createCalculation(calculation) {
        try {
            const keys = [
                'title',
                'currency',
                'exchange',
                'materialPrice',
                'paintPrice',
                'waste',
                'margin',
                'areas',
                'weights',
                'materials',
                'assambly',
                'packages',
                'items',
            ]
            Object.keys(calculation).forEach(key => {
                if (!keys.includes(key)) {
                    delete calculation[key]
                }
            })
            const response = await API.post('/calculations', calculation)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getCalculations() {
        try {
            return await API.get('/calculations')
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getCalculation(id) {
        try {
            const response = await API.get(`/calculations/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateCalculation(id, calculation) {
        try {
            const keys = [
                'title',
                'currency',
                'exchange',
                'materialPrice',
                'paintPrice',
                'waste',
                'margin',
                'areas',
                'weights',
                'materials',
                'assambly',
                'packages',
                'items',
            ]
            Object.keys(calculation).forEach(key => {
                if (!keys.includes(key)) {
                    delete calculation[key]
                }
            })
            const response = await API.put(`/calculations/${id}`, calculation)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateCalculationInSettings(id, calculation) {
        try {
            const keys = [
                'title',
                'currency',
                'exchange',
                'materialPrice',
                'paintPrice',
                'waste',
                'margin',
                'areas',
                'weights',
                'materials',
                'assambly',
                'packages',
                'items',
            ]
            Object.keys(calculation).forEach(key => {
                if (!keys.includes(key)) {
                    delete calculation[key]
                }
            })
            const response = await API.put(
                `/calculations/calculation-in-settings/${id}`,
                calculation
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteCalculation(id) {
        try {
            const response = await API.delete(`/calculations/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    /**
     * Projects
     */
    async getProjects({ projectId = undefined, archive = false }) {
        try {
            if (projectId) {
                const {
                    data: { projects },
                } = await API.get(`/projects?id=${projectId}`)
                return projects
            }
            const {
                data: { projects },
            } = await API.get(`/projects?archive=${archive}`)
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getClosedProjects({ startDate, endDate }) {
        try {
            const {
                data: { projects },
            } = await API.get(
                `/projects/closed-projects?startDate=${startDate}&endDate=${endDate}`
            )
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getProject(projectId) {
        try {
            const {
                data: { project },
            } = await API.get(`/projects/${projectId}`)
            return project
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getLiteProjects({
        archive = false,
        all = false,
        closing = false,
        signal,
    } = {}) {
        try {
            let query = ''
            if (archive) {
                query += `?archive=${archive}`
            }
            if (closing) {
                if (query.length > 0) {
                    query += `&closing=${closing}`
                } else {
                    query += `?closing=${closing}`
                }
            }
            if (all) {
                if (query.length > 0) {
                    query += `&all=${all}`
                } else {
                    query += `?all=${all}`
                }
            }

            const config = {}
            if (signal) {
                config.signal = signal
            }

            const {
                data: { projects },
            } = await API.get(`/projects/lite${query}`, config)
            return projects
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                throw error
            }
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getWoProjects() {
        try {
            return await shareLookup('projects:wo-projects', async () => {
                const {
                    data: { projects },
                } = await API.get(`/projects/wo-projects`)
                return projects
            })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    // Get projects without read projects permission
    async getLiteFreeProjects({
        archive = false,
        all = false,
        closing = false,
        signal,
    } = {}) {
        try {
            let query = ''
            if (archive) {
                query += `?archive=${archive}`
            }
            if (closing) {
                if (query.length > 0) {
                    query += `&closing=${closing}`
                } else {
                    query += `?closing=${closing}`
                }
            }
            if (all) {
                if (query.length > 0) {
                    query += `&all=${all}`
                } else {
                    query += `?all=${all}`
                }
            }

            const config = {}
            if (signal) {
                config.signal = signal
            }

            const {
                data: { projects },
            } = await API.get(`/projects/lite-free${query}`, config)
            return projects
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                throw error
            }
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getLiteProjectsByUser() {
        try {
            const {
                data: { projects },
            } = await API.get(`/projects/liteByUser`)
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getAllLiteProjectsByUser() {
        try {
            const {
                data: { projects },
            } = await API.get(`/projects/liteByUserAll`)
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getLiteProjectsByClient(client) {
        try {
            const {
                data: { projects },
            } = await API.get(`/projects/liteByClient?clientId=${client}`)
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getGenericProjects() {
        try {
            const {
                data: { projects },
            } = await API.get(`/projects/generics`)
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getProjectQuotes(id) {
        try {
            const {
                data: { quotes },
            } = await API.get(`/projects/quotes${id ? `?id=${id}` : ''}`)
            return quotes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getOCsAndPositions(projectId, companyId) {
        try {
            let queryParams = `projectId=${projectId}`
            if (companyId) {
                queryParams += `&companyId=${companyId}`
            }
            const {
                data: { OCs, positions },
            } = await API.get(`/projects/ocs-and-positions?${queryParams}`)
            return { OCs, positions }
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getInformProjectsByScopes() {
        try {
            const {
                data: { rows },
            } = await API.get(`/projects/inform-by-scopes`)
            return rows
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    /**
     * Tasks
     */

    async createTask(params) {
        try {
            const {
                data: { task },
            } = await API.post('/tasks', params)
            return task
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteTask(params) {
        try {
            const {
                data: { task },
            } = await API.delete(
                `/tasks?id=${params.id}&assignedTo=${params.assignedTo}`
            )
            return task
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateTask(id, params) {
        try {
            Object.keys(params).map(key => {
                if (key != 'archive' && !params[key]) {
                    delete params[key]
                }
            })
            delete params['timestamp']
            delete params['createdOn']
            delete params['updatedOn']
            const {
                data: { task },
            } = await API.put(`/tasks?id=${id}`, params)
            return task
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getTasks(archive = undefined) {
        try {
            const {
                data: { tasks },
            } = await API.get(`/tasks?archive=${archive}`)
            return tasks
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getTaskChilds(taskId, assignedTo) {
        try {
            const {
                data: { taskChilds },
            } = await API.get(
                `/tasks/childs?taskId=${taskId}&assignedTo=${assignedTo}`
            )
            return taskChilds
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createTaskChild(params) {
        try {
            const {
                data: { taskChild },
            } = await API.post(`/tasks/createChild`, params)
            return taskChild
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateTaskChild(childId, params) {
        try {
            const {
                data: { taskChild },
            } = await API.put(`/tasks/updateChild?childId=${childId}`, params)
            return taskChild
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteTaskChild(taskId, childId, assignedTo) {
        try {
            await API.delete(
                `/tasks/deleteChild?taskId=${taskId}&childId=${childId}&assignedTo=${assignedTo}`
            )
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getAssignedTasks(projectId = undefined, quoteId = undefined) {
        try {
            const {
                data: { tasks },
            } = await API.get(
                `/tasks/assigned?${projectId ? `projectId=${projectId}` : ''}${
                    quoteId
                        ? projectId
                            ? `&quoteId=${quoteId}`
                            : `quoteId=${quoteId}`
                        : ''
                }`
            )
            return tasks
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    // stock endpoints

    async uploadFile(file) {
        try {
            const {
                data: { msg },
            } = await API.post(`/stock/upload-file`, { file })
            return msg
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getReservedStock(projectId) {
        try {
            const {
                data: { stock },
            } = await API.get(
                `/stock/reserved-stock?${
                    projectId ? `projectId=${projectId}` : ''
                }`
            )
            return stock
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getGenericProjectReserves(projectId) {
        try {
            const {
                data: { stock },
            } = await API.get(
                `/stock/generic-project-reserves?projectId=${projectId}`
            )
            return stock
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getGenericProjectStock(projectId) {
        try {
            const {
                data: { stock },
            } = await API.get(
                `/stock/generic-project-stock?projectId=${projectId}`
            )
            return stock
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getProjectStockForBOM(projectId) {
        if (isGenericStockProject(projectId)) {
            return this.getGenericProjectReserves(projectId)
        }
        return this.getReservedStock(projectId)
    },

    async getStock() {
        try {
            const {
                data: { stock },
            } = await API.get(`/stock`)
            return stock
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getRawStock() {
        try {
            const {
                data: { stock },
            } = await API.get(`/stock/rawStock`)
            return stock
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createReserve(params) {
        try {
            const {
                data: { reserve },
            } = await API.post(`/stock/reserve`, params)
            return reserve
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getReserves(projectId = '', status = '') {
        try {
            const {
                data: { reserves },
            } = await API.get(
                `/stock/reserve?projectId=${projectId}&status=${status}`
            )
            return reserves
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateReserve(id, projectId, params) {
        try {
            const {
                data: { reserve },
            } = await API.put(
                `/stock/reserve?id=${id}&projectId=${projectId}`,
                params
            )
            return reserve
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async releaseReserve(params) {
        try {
            const {
                data: { reserve },
            } = await API.post(`/stock/release`, params)
            return reserve
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteReserve(projectId, reserveId) {
        try {
            await API.delete(
                `/stock/reserve?id=${reserveId}&projectId=${projectId}`
            )
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getRequests(id) {
        try {
            const {
                data: { requests },
            } = await API.get(`/stock/request${id ? `?id=${id}` : ''}`)
            return requests
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async addRequest(params) {
        try {
            Object.keys(params).forEach(key => {
                if (!params[key]) {
                    delete params[key]
                }
            })
            await API.post(`/stock/request`, params)
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateRequest(params) {
        try {
            const {
                data: { request },
            } = await API.put(`/stock/request`, params)
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteRequest(id) {
        try {
            await API.delete(`/stock/request?id=${id}`)
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    /**
     *
     * Purchases
     */
    async purchasesChangelog(params) {
        try {
            const { start, end } = params
            const {
                data: { purchases },
            } = await API.get(`/purchase/changelog?start=${start}&end=${end}`)
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPurchases(params) {
        try {
            const { status, startDate, endDate } = params
            const {
                data: { purchases },
            } = await API.get(
                `/purchase?startDate=${startDate}&endDate=${endDate}&status=${status}`
            )
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getNoOrderPurchases(params = {}) {
        try {
            const {
                data: { purchases },
            } = await API.get(`/purchase/no-order-purchases`, { params })
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async addNoOrderPurchaseNote(params) {
        try {
            const {
                data: { result },
            } = await API.put(`/purchase/add-no-order-purchase-note`, params)
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async addPurchaseItemNote(params) {
        try {
            const {
                data: { result },
            } = await API.put(`/purchase/add-item-note`, params)
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async addPurchaseNote(params) {
        try {
            const {
                data: { result },
            } = await API.put(`/purchase/add-purchase-note`, params)
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createPurchaseRequest(params, options = {}) {
        try {
            const config = {}
            if (options.idempotencyKey) {
                config.headers = {
                    'Idempotency-Key': options.idempotencyKey,
                }
            }
            const {
                data: { request },
            } = await API.post(`/purchase`, params, config)
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePurchaseRequest(id, params) {
        try {
            const keys = [
                'companyId',
                'projectId',
                'id',
                'buyer',
                'dueDate',
                'deliveryLocation',
                'importation',
                'incoterm',
                'notes',
                'type',
                'status',
                'rejectedNotes',
                'purchaseOrders',
                'closingFile',
                'totalEntries',
                'suppliersRejected',
            ]
            const { notificationId, ...paramsToUpdate } = params
            Object.keys(paramsToUpdate).forEach(key => {
                if (!keys.includes(key) || params[key] === undefined) {
                    delete paramsToUpdate[key]
                }
            })
            const { data: request } = await API.put(
                `/purchase?id=${id}&notificationId=${notificationId}`,
                paramsToUpdate
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    // async notifyRejectedSuppliers(id, params) {},
    async createClosedPurchase(id, params) {
        try {
            const { data: request } = await API.post(
                `/purchase/createClosedPurchase/?id=${id}`,
                params
            )
            return request
        } catch (error) {
            const errorResponse = error
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async assignRequest(id, params) {
        try {
            const { notificationId, ...body } = params
            const { data: request } = await API.put(
                `/purchase/assign?id=${id}&notificationId=${notificationId}`,
                body
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateItemByAccountant(params) {
        try {
            const { data: request } = await API.put(
                `/purchase/updateItemByAccountant`,
                params
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async uploadInvoices(params) {
        try {
            const { data: request } = await API.put(
                `/purchase/uploadInvoices`,
                params
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePurchaseInvoices(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/updatePurchaseInvoices`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deletePurchaseInvoice(params) {
        try {
            const { data: result } = await API.delete(
                `/purchase/deletePurchaseInvoice`,
                {
                    data: params,
                }
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteNoOrderInvoice(params) {
        try {
            const {
                data: result,
            } = await API.delete(`/purchase/no-order-invoice`, { data: params })
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePurchaseInvoice(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/purchase-invoice`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePurchaseEntryAccountant(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/purchase-entry-accountant`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePurchaseEntrySap(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/purchase-entry-sap`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createPurchaseRequestsItem(requestId, params) {
        try {
            const {
                data: { item },
            } = await API.post(`/purchase/items?requestId=${requestId}`, params)
            return item
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePurchaseRequestsItem(requestId, itemId, params) {
        try {
            const {
                data: { item },
            } = await API.put(
                `/purchase/items?requestId=${requestId}&itemId=${itemId}`,
                params
            )
            return item
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deletePurchaseQuoteItem(requestId, itemId) {
        try {
            const {
                data: { request },
            } = await API.delete(
                `/purchase/quote-item?requestId=${requestId}&itemId=${itemId}`
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async addQuoteToRequestsItem(params) {
        try {
            const {
                data: { quote },
            } = await API.post(`/purchase/quotes`, params)
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateQuoteToRequestsItem(quoteId, params) {
        try {
            const {
                data: { quote },
            } = await API.put(`/purchase/quotes?id=${quoteId}`, params)
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async selectQuoteToRequestsItem(quoteId, params) {
        try {
            const {
                data: { request },
            } = await API.put(
                `/purchase/quotes/selectQuote?id=${quoteId}`,
                params
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deletePurchaseRequest(requestId, options = {}) {
        try {
            const config = {}
            if (options.signal) {
                config.signal = options.signal
            }
            await API.delete(`/purchase?id=${requestId}`, config)
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                throw error
            }
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deletePurchaseRequestQuote(requestId, itemId, quoteId) {
        try {
            await API.delete(
                `/purchase/quotes?requestId=${requestId}&itemId=${itemId}&quoteId=${quoteId}`
            )
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deletePurchaseRequestItem(requestId, itemId) {
        try {
            await API.delete(
                `/purchase/items?requestId=${requestId}&itemId=${itemId}`
            )
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async addEntryToRequestsItem(params) {
        try {
            const {
                data: { entry },
            } = await API.post(`/purchase/entries`, params)
            return entry
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async addReceptionEntry(params) {
        try {
            const { data } = await API.post(
                `/purchase/reception-entries`,
                params
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async verifyReceptionEntryHseq(params) {
        try {
            const { data } = await API.put(
                `/purchase/reception-entries/hseq-verify`,
                params
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async uploadReceptionEntryCertificate(params) {
        try {
            const { data } = await API.put(
                `/purchase/reception-entries/signed-certificate`,
                params
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async sendReceptionCertificateSignatureEmail(params) {
        try {
            const { data } = await API.post(
                `/purchase/reception-entries/send-signature-email`,
                params
            )
            return data
        } catch (error) {
            throw new Error(
                getApiErrorMessage(
                    error,
                    'Could not send the acceptance certificate email. Please try again.'
                )
            )
        }
    },

    async getReceptionCertificateForSignature(params) {
        try {
            const {
                data: { receptionCertificate },
            } = await API.get(
                `/receptionCertificates/getReceptionCertificate/?params=${params}`
            )
            return receptionCertificate
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async signReceptionCertificate(params) {
        try {
            const { data } = await API.post(
                `/receptionCertificates/signReceptionCertificate/?params=${params.params}`,
                {
                    name: params.name,
                    documentId: params.documentId,
                    signatureBase64: params.signatureBase64,
                }
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async sendReceptionCertificateSignatureConfirmation(params) {
        try {
            const { data } = await API.post(
                `/receptionCertificates/sendSignatureConfirmation/?params=${params.params}`,
                {
                    pdfStoragePath: params.pdfStoragePath,
                    pdfFileName: params.pdfFileName,
                }
            )
            return data
        } catch (error) {
            throw new Error(
                getApiErrorMessage(
                    error,
                    'Could not send the signed certificate confirmation email. Please try again.'
                )
            )
        }
    },

    async updateEntryToRequestsItem(params) {
        try {
            const { entryId, ...paramsToSave } = params
            const {
                data: { entry },
            } = await API.put(`/purchase/entries?id=${entryId}`, paramsToSave)
            return entry
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async rejectPurchaseInvoice(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/rejectPurchaseInvoice`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async rejectNoOrderPurchase(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/rejectNoOrderPurchase`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async linkNoOrderPurchaseInvoiceToPurchase(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/link-no-order-purchase-invoice`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async linkInvoiceToPurchaseWithoutOrder(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/link-invoice-to-purchase-without-order`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPurchasesClosed(params) {
        try {
            const {
                createdBy,
                buyer,
                projectId,
                status,
                projectManager,
            } = params
            let query = ''
            if (createdBy) {
                query += `createdBy=${createdBy}`
            }
            if (buyer) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `buyer=${buyer}&`
            }
            if (projectId) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `projectId=${projectId}&`
            }
            if (status) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `status=${status}`
            }
            if (projectManager) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `projectManager=${projectManager}`
            }
            const {
                data: { purchases },
            } = await API.get(`/purchase/closed?${query}`)
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPurchasesClosedByDate(params) {
        try {
            const { initialDate, finishDate, projectManager } = params
            let query = ''

            if (initialDate && finishDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `initialDate=${initialDate}`
                query += `&finishDate=${finishDate}`
            }
            if (projectManager) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `projectManager=${projectManager}`
            }
            const { data } = await API.get(`/purchase/closedByDate?${query}`)
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPurchasesClosedNextPage({ jobId, pageToken }) {
        try {
            const { data } = await API.get(
                `/purchase/closedByDate/next?jobId=${jobId}&pageToken=${encodeURIComponent(
                    pageToken
                )}`
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getClosedPurchasesForAccountantHistory({ initialDate, finishDate }) {
        try {
            const {
                data: { purchases },
            } = await API.get(
                `/purchase/closed-accountant-history?initialDate=${initialDate}&finishDate=${finishDate}`
            )
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async findAccountantInvoiceByConsecutive(consecutive) {
        try {
            const {
                data: { invoices },
            } = await API.get(
                `/purchase/accountant-invoice-by-consecutive?consecutive=${encodeURIComponent(
                    consecutive
                )}`
            )
            return invoices
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getClosedAccountantInvoices({
        startDate,
        endDate,
        dateBasis = 'invoice',
    } = {}) {
        try {
            const {
                data: { invoices },
            } = await API.get(
                `/purchase/accountant-closed-invoices-history?startDate=${startDate}&endDate=${endDate}&dateBasis=${dateBasis}`
            )
            return invoices
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPurchasesClosedWithComex(params) {
        try {
            const { initialDate, finishDate, comex } = params
            let query = ''

            if (initialDate && finishDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `initialDate=${initialDate}&finishDate=${finishDate}`
            }
            if (comex === true || comex == null) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `withComex=true`
            } else {
                if (query.length > 0) {
                    query += '&'
                }
                query += 'withComex=false'
            }
            const {
                data: { purchases },
            } = await API.get(`/purchase/closedWithComex${'?' + query}`)
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message ||
                    errorResponse.error ||
                    'Error fetching purchases'
            )
        }
    },
    async getPurchasesWithComex(params) {
        try {
            const { initialDate, finishDate } = params
            let query = ''

            if (initialDate && finishDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `initialDate=${initialDate}&finishDate=${finishDate}`
            }
            const {
                data: { purchases },
            } = await API.get(`/purchase/withComex${'?' + query}`)
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message ||
                    errorResponse.error ||
                    'Error fetching purchases'
            )
        }
    },

    async getPurchasesRejected() {
        try {
            const {
                data: { purchases },
            } = await API.get(`/purchase/rejected-log`)
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePurchaseByComex(params) {
        try {
            const {
                data: { request },
            } = await API.put(`/purchase/update-comex`, params)
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPurchasesComex() {
        try {
            const {
                data: { purchases },
            } = await API.get(`/purchase/purchases-with-comex`)
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    /**
     *
     * Suppliers
     */

    async getSupplierQuestions(params) {
        try {
            const {
                data: { questions },
            } = await API.get(
                `/suppliers/questions?language=${params.language}`
            )
            return questions
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getSuppliers() {
        try {
            const {
                data: { suppliers },
            } = await API.get(`/suppliers`)
            return suppliers
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getLiteSuppliers() {
        try {
            const {
                data: { suppliers },
            } = await API.get(`/suppliers/lite`)
            return suppliers
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getSupplier(id) {
        try {
            const {
                data: { supplier },
            } = await API.get(`/suppliers/${id}`)
            return supplier
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getSupplierEvaluations(params) {
        try {
            const {
                data: { evaluations },
            } = await API.get(
                `/suppliers/evaluations?supplierId=${params.supplierId}`
            )
            return evaluations
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getEvaluationsSuppliers(params) {
        try {
            const { initialDate, finishDate } = params
            let query = ''
            if (initialDate && finishDate) {
                query += `?initialDate=${initialDate}&finishDate=${finishDate}`
            }
            const {
                data: { evaluations },
            } = await API.get(`/suppliers/all-evaluations${query}`)
            return evaluations
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createSupplierEvaluation(evaluation) {
        try {
            const { data } = await API.post(
                '/suppliers/evaluations',
                evaluation
            )
            return data?.evaluation ?? data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateSupplierEvaluation(params) {
        try {
            const {
                data: { evaluation },
            } = await API.put(`/suppliers/evaluations`, params)
            return evaluation
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createSupplier(params) {
        try {
            const {
                name,
                picture,
                country,
                city,
                state,
                language,
                currency,
                nit,
                enabled,
                sapStatus,
                note,
            } = params
            const {
                data: { supplier },
            } = await API.post('/suppliers', {
                name,
                country,
                picture,
                city,
                state,
                language,
                currency,
                nit,
                enabled,
                sapStatus,
                note,
            })
            return supplier
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateSupplier(supplierId, params) {
        try {
            const {
                data: { supplier },
            } = await API.put(`/suppliers/${supplierId}`, params)
            return supplier
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteSupplier(supplierId) {
        try {
            await API.delete(`/suppliers/${supplierId}`)
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getContactsBySupplier(params) {
        try {
            const {
                data: { contacts },
            } = await API.get(
                `/contacts/contactsBySupplier?supplierId=${params.supplierId}`
            )
            return contacts
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    /**
     *
     * Foreign Trade
     */

    async createExportation(exportation) {
        try {
            Object.keys(exportation).forEach(key => {
                if (!exportation[key]) {
                    delete exportation[key]
                }
            })
            const response = await API.post('/foreignTrade', exportation)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateExportation(exportationId, params, deletedBL) {
        try {
            params.deletedBL = deletedBL
            const {
                data: { exportation },
            } = await API.put(`/foreignTrade/${exportationId}`, params)
            return exportation
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getExportations() {
        try {
            const {
                data: { exportations },
            } = await API.get(`/foreignTrade`)
            return exportations
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    /**
     * Part Numbers
     */
    async createPartNumber(params) {
        try {
            const {
                data: { partNumbersCreated },
            } = await API.put(`/partNumber/`, params)

            return partNumbersCreated
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deletePartNumber(projectId, partNumberCode) {
        try {
            await API.delete(
                `/partNumber/?projectId=${projectId}&partNumberCode=${partNumberCode}`
            )
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPartNumbersInKeys({ projectId }) {
        try {
            const {
                data: { response },
            } = await API.get(
                `/partNumber/part-numbers-in-keys?projectId=${projectId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPartNumbersProductionData(projectId, companyId) {
        try {
            let queryParams = `projectId=${projectId}`
            if (companyId) {
                queryParams += `&companyId=${companyId}`
            }
            const {
                data: { partNumbersProductionData },
            } = await API.get(`/partNumber/production-data?${queryParams}`)
            return partNumbersProductionData
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    // Products
    async getProducts() {
        try {
            const response = await API.get(`/settings/products`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteProduct(params) {
        try {
            const response = await API.delete(
                `/settings/products?settingId=${params.settingId}&productId=${params.productId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createProduct(params) {
        try {
            const {
                data: { product },
            } = await API.post(`/settings/products`, params)
            return product
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateProduct(params) {
        try {
            const { productId, ...paramsToUpdate } = params
            const {
                data: { product },
            } = await API.put(`/settings/products/${productId}`, paramsToUpdate)
            return product
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    /**
     * Part Numbers
     */
    async getPartNumbers(projectId, partNumberId, companyId) {
        try {
            let queryParams = `projectId=${projectId}`
            if (partNumberId) {
                queryParams += `&partNumberId=${partNumberId}`
            }
            if (companyId) {
                queryParams += `&companyId=${companyId}`
            }
            const {
                data: {
                    partNumbers: { response },
                },
            } = await API.get(`/partNumber?${queryParams}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updatePartNumbersByXLSX(params) {
        try {
            const { data } = await API.put('/partNumber/by-xlsx', params)
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /**
     * Work Orders
     */
    async createWorkOrder(params) {
        try {
            const {
                data: { workOrder },
            } = await API.post(`/workOrder`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async copyWorkOrder(params) {
        try {
            const body = {
                partNumbers:
                    params.partNumbers != undefined ? params.partNumbers : {},
                projectId: params.projectId,
                status: params.status,
                weight: params.weight,
                assemblyWorkOrder: params.assemblyWorkOrder,
                ...(params.notes ? { notes: params.notes } : {}),
            }
            const {
                data: { workOrder },
            } = await API.post(`/workOrder/copy`, body)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteWorkOrder(id) {
        try {
            const {
                data: { message },
            } = await API.delete(`/workOrder?workOrderId=${id}`)
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateWorkOrderDescription(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/description`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateWorkOrderClassification(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/classification`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createWorkOrderItem(params) {
        try {
            const {
                data: { item },
            } = await API.post(`/workOrder/create-item`, params)
            return item
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createMultipleWorkOrderItems(params) {
        try {
            const {
                data: { items },
            } = await API.post(`/workOrder/create-multiple-items`, {
                companyId: params.companyId,
                workOrderId: params.workOrderId,
                settingFinishId: params.settingFinishId,
                settingProcessesId: params.settingProcessesId,
                items: params.items,
            })
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateWorkOrderItem(params) {
        try {
            const {
                data: { item },
            } = await API.put(`/workOrder/update-item`, params)
            return item
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    // TEMPORARY: remove when temp finish edit feature is retired
    async tempUpdateWorkOrderItemFinishAndUCCode(params) {
        try {
            const {
                data: { item },
            } = await API.put(
                `/workOrder/temp-update-finish-and-uc-code`,
                params
            )
            return item
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateItemProcesses(params) {
        try {
            const {
                data: { item },
            } = await API.put(`/workOrder/update-item-processes`, params)
            return item
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async setBatchProcesses(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/batch-processes`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async registerProduction(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/item-production`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async registerAssemblyProduction(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/assembly-production`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async registerAssemblyProductionBatch(params) {
        try {
            const { data } = await API.put(
                `/workOrder/assembly-production-batch`,
                params
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async assignQuantityFromStock(params) {
        try {
            const {
                data: { partNumber },
            } = await API.put(`/workOrder/assign-from-stock`, params)
            return partNumber
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendToNextProcess(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/item-delivery`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async receiveItems(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/receive-items`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendBatchToNextProcess(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/batch-delivery`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async registerQuality(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/item-quality`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async registerProcessQuality(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/process-quality`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async markBatchQuality(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/batch-quality`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async registerBatchQualityPerProcess(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/batch-quality-per-process`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createAssemblyWO(params) {
        try {
            const {
                data: { workOrder },
            } = await API.post(`/workOrder/assembly-wo`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async saveAssemblyItems(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/assembly-items`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async removeAssemblyItem(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/remove-assembly-item`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateAssemblyItem(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/update-assembly-item`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async addFilesToItems(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/add-files-to-items`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteWorkOrderItem(params) {
        try {
            const {
                data: { response },
            } = await API.delete(
                `/workOrder/delete-item?workOrderId=${params.workOrderId}&partNumberId=${params.partNumberId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteWorkOrderAllItems(workOrderId) {
        try {
            const {
                data: { response },
            } = await API.delete(
                `/workOrder/delete-item?workOrderId=${workOrderId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateWorkOrder(params) {
        try {
            const { data } = await API.put(`/workOrder`, params)
            const notAvailablePartNumbers = data?.notAvailablePartNumbers
            if (notAvailablePartNumbers?.length > 0) {
                throw {
                    name: 'NotAvailablePartNumbers',
                    notAvailablePartNumbers,
                }
            }
            return data.workOrder
        } catch (error) {
            if (error?.notAvailablePartNumbers?.length > 0) {
                throw error
            }
            const errorResponse = error.response?.data
            throw new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
        }
    },
    async closeWorkOrder(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/close`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async forceFinishWorkOrder(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/force-finish`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getLatestWorkOrders(params) {
        try {
            let url = '/workOrder/latest'
            const queryParams = []
            if (params && params.status)
                queryParams.push(`status=${params.status}`)
            if (params && params.startDate)
                queryParams.push(`startDate=${params.startDate}`)
            if (params && params.endDate)
                queryParams.push(`endDate=${params.endDate}`)
            if (queryParams.length) url += `?${queryParams.join('&')}`
            const {
                data: { latestWOs },
            } = await API.get(url)
            return latestWOs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateWorkOrderNotes(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/notes`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateWorkOrderProductType(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/product-type`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateWorkOrderType(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/type`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateWorkOrderOriginWorkOrders(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/origin-work-orders`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateWorkOrderAllowWoUpdates(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/allow-wo-updates`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateProductionInfo(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/productionInfo`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getWOPackingLists(params) {
        try {
            const { workOrderId, workOrderCode, companyId } = params
            let queryParams = `workOrderId=${workOrderId}&workOrderCode=${workOrderCode}`
            if (companyId) {
                queryParams += `&companyId=${companyId}`
            }
            const {
                data: { packingLists },
            } = await API.get(`/workOrder/packing-lists?${queryParams}`)
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getClosedWorkOrders(params) {
        try {
            const { startDate, endDate, projectId } = params
            return await collectWorkOrderPages(async (limit, offset) => {
                const {
                    data: { workOrders },
                } = await API.get(
                    `/workOrder/closed?${
                        startDate ? `startDate=${startDate}&` : ''
                    }${endDate ? `endDate=${endDate}&` : ''}${
                        projectId ? `projectId=${projectId}&` : ''
                    }limit=${limit}&offset=${offset}`
                )
                return workOrders
            })
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async getAssemblyWorkOrders() {
        try {
            const {
                data: { assemblyWOs },
            } = await API.get(`/workOrder/assembly`)
            return assemblyWOs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getDailyWorkOrders(params) {
        try {
            const { startDate, endDate } = params

            const {
                data: { workOrders },
            } = await API.get(
                `/workOrder/daily-wo?startDate=${startDate}&endDate=${endDate}`
            )

            return workOrders
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async getWorkOrdersByStatus(params) {
        try {
            const { startDate, endDate, status } = params
            const {
                data: { workOrders },
            } = await API.get(
                `/workOrder/by-status?${
                    startDate ? `startDate=${startDate}&` : ''
                }${endDate ? `endDate=${endDate}&` : ''}${
                    status ? `status=${status}` : ''
                }`
            )
            return workOrders
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },
    async getWorkOrdersProducedByKg(params) {
        try {
            const { startDate, endDate } = params
            const {
                data: { workOrders },
            } = await API.get(
                `/workOrder/produced-by-kg?startDate=${startDate}&endDate=${endDate}`
            )
            return workOrders
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },
    async getWorkOrdersStatistics(params) {
        try {
            const { startDate, endDate, status } = params
            const {
                data: { workOrders },
            } = await API.get(
                `/workOrder/statistics?startDate=${startDate}&endDate=${endDate}&status=${status}`
            )
            return workOrders
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getHistoryLog(date) {
        try {
            const historyLog = await API.get(
                `/workOrder/history-log?date=${date}`
            )
            return historyLog.data || false
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },
    async getAllWorkOrdersVersion(date) {
        try {
            const {
                data: { workOrders },
            } = await API.get(`/workOrder/all-by-version?date=${date}`)
            return workOrders
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createWorkOrderHistoryLog(date) {
        try {
            const {
                data: { workOrders },
            } = await API.post(`/workOrder/history-log?date=${date}`, null)
            return workOrders
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },
    async createReprocessWorkOrder(params) {
        try {
            const { data } = await API.post(`/workOrder/reprocess-wo`, params)
            const notAvailablePartNumbers = data?.notAvailablePartNumbers
            if (notAvailablePartNumbers?.length > 0) {
                throw {
                    name: 'NotAvailablePartNumbers',
                    notAvailablePartNumbers,
                }
            }
            return data.workOrder
        } catch (error) {
            if (error?.notAvailablePartNumbers?.length > 0) {
                throw error
            }
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getApprovedWorkOrders() {
        try {
            return await collectWorkOrderPages(async (limit, offset) => {
                const {
                    data: { workOrders },
                } = await API.get(
                    `/workOrder/approved-wo?limit=${limit}&offset=${offset}`
                )
                return workOrders
            })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getLiteWorkOrders() {
        try {
            const {
                data: { latestWOs },
            } = await API.get(`/workOrder/lite`)
            return latestWOs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getLiteClosedWorkOrders() {
        try {
            const {
                data: { latestWOs },
            } = await API.get(`/workOrder/lite-closed`)
            return latestWOs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getActiveWorkOrders(limit = 100, offset = 0) {
        try {
            const { data } = await API.get(`/workOrder/active-wo`, {
                params: {
                    limit,
                    offset,
                },
            })
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async overrideLog(params) {
        try {
            const {
                data: { cancelledLog },
            } = await API.put(`/workOrder/override-log`, params)
            return cancelledLog
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /*
        PROCESSES
    */
    async getProcesses(settingId) {
        try {
            const {
                data: { processes },
            } = await API.get(`/settings/processes?settingId=${settingId}`)
            return processes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProcess(settingId, processId) {
        try {
            const {
                data: { process },
            } = await API.get(
                `/settings/processes?settingId=${settingId}&processId=${processId}`
            )
            return process
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createProcess(params) {
        try {
            const {
                data: { process },
            } = await API.post(`/settings/processes`, params)
            return process
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateProcess(params) {
        try {
            const { processId, ...paramsToUpdate } = params
            const {
                data: { process },
            } = await API.put(
                `/settings/processes/${processId}`,
                paramsToUpdate
            )
            return process
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteProcess(params) {
        try {
            const {
                data: { message },
            } = await API.delete(
                `/settings/processes?settingId=${params.settingId}&processId=${params.processId}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    /*
        Product Types
    */
    async getProductTypes(settingId) {
        try {
            const {
                data: { productTypes },
            } = await API.get(`/settings/product-type?settingId=${settingId}`)
            return productTypes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createProductType(params) {
        try {
            const {
                data: { productType },
            } = await API.post(`/settings/product-type`, params)
            return productType
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateProductType(params) {
        try {
            const { settingId, ...paramsToUpdate } = params
            const {
                data: { productType },
            } = await API.put(
                `/settings/product-type/${settingId}`,
                paramsToUpdate
            )
            return productType
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteProductType(params) {
        try {
            const {
                data: { message },
            } = await API.delete(
                `/settings/product-type?settingId=${params.settingId}&id=${params.productTypeId}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /*
        NCCodes
    */
    async getNCCodes(settingId) {
        try {
            const {
                data: { codes },
            } = await API.get(
                `/settings/quality/ncCodes?settingId=${settingId}`
            )
            return codes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getNCCode(settingId, codeId) {
        try {
            const {
                data: { code },
            } = await API.get(
                `/settings/quality/ncCodes?settingId=${settingId}&codeId=${codeId}`
            )
            return code
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createNCCode(params) {
        try {
            const {
                data: { code },
            } = await API.post(`/settings/quality/ncCodes`, params)
            return code
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateNCCode(params) {
        try {
            const { codeId, ...paramsToUpdate } = params
            const {
                data: { code },
            } = await API.put(
                `/settings/quality/ncCodes/${codeId}`,
                paramsToUpdate
            )
            return code
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteNCCode(params) {
        try {
            const {
                data: { message },
            } = await API.delete(
                `/settings/quality/ncCodes?settingId=${params.settingId}&id=${params.id}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /*
        Rejection Reasons
    */
    async getRejectionReasons(settingId) {
        try {
            const {
                data: { reasons },
            } = await API.get(
                `/settings/quality/rejectionReasons?settingId=${settingId}`
            )
            return reasons
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getRejectionReason(settingId, reasonId) {
        try {
            const {
                data: { reason },
            } = await API.get(
                `/settings/quality/rejectionReasons?settingId=${settingId}&rejectionReasonId=${reasonId}`
            )
            return reason
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createRejectionReason(params) {
        try {
            const {
                data: { reason },
            } = await API.post(`/settings/quality/rejectionReasons`, params)
            return reason
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateRejectionReason(params) {
        try {
            const { rejectionReasonId, ...paramsToUpdate } = params
            const {
                data: { reason },
            } = await API.put(
                `/settings/quality/rejectionReasons/${rejectionReasonId}`,
                paramsToUpdate
            )
            return reason
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteRejectionReason(params) {
        try {
            const {
                data: { message },
            } = await API.delete(
                `/settings/quality/rejectionReasons?settingId=${params.settingId}&id=${params.id}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /*
        Finish And UC Code
    */
    async getFinishesAndUCCodes(settingId) {
        try {
            const {
                data: { finishes },
            } = await API.get(
                `/settings/finish-and-uc-code?settingId=${settingId}`
            )
            return finishes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getFinishAndUCCode(settingId, id) {
        try {
            const {
                data: { finish },
            } = await API.get(
                `/settings/finish-and-uc-code?settingId=${settingId}&id=${id}`
            )
            return finish
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createFinishAndUCCode(params) {
        try {
            const {
                data: { finish },
            } = await API.post(`/settings/finish-and-uc-code`, params)
            return finish
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createFinishAndUCCodeBatch(params) {
        try {
            const { data } = await API.post(
                `/settings/finish-and-uc-code/batch`,
                params
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data
            if (errorResponse) {
                // if backend returned data with exceptions, still return it
                if (errorResponse.allItems || errorResponse.codeExceptions) {
                    return errorResponse
                }
                throw new Error(
                    errorResponse.message ||
                        errorResponse.error ||
                        error.message
                )
            }
            throw error
        }
    },
    async clearFinishAndUCCodeExceptions(settingId) {
        try {
            const { data } = await API.delete(
                `/settings/finish-and-uc-code/exceptions?settingId=${settingId}`
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateFinishAndUCCode(params) {
        try {
            const { id, ...paramsToUpdate } = params
            const {
                data: { finish },
            } = await API.put(
                `/settings/finish-and-uc-code/${id}`,
                paramsToUpdate
            )
            return finish
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteFinishAndUCCode(params) {
        try {
            const {
                data: { message },
            } = await API.delete(
                `/settings/finish-and-uc-code?settingId=${params.settingId}&id=${params.id}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /*
        HS codes
    */
    async getHSCodes(settingId) {
        try {
            const {
                data: { codes },
            } = await API.get(`/settings/hs-code?settingId=${settingId}`)
            return codes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getHSCode(settingId, id) {
        try {
            const {
                data: { code },
            } = await API.get(
                `/settings/hs-code?settingId=${settingId}&id=${id}`
            )
            return code
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createHSCode(params) {
        try {
            const {
                data: { code },
            } = await API.post(`/settings/hs-code`, params)
            return code
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateHSCode(params) {
        try {
            const { id, ...paramsToUpdate } = params
            const {
                data: { code },
            } = await API.put(`/settings/hs-code/${id}`, paramsToUpdate)
            return code
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteHSCode(params) {
        try {
            const {
                data: { message },
            } = await API.delete(
                `/settings/hs-code?settingId=${params.settingId}&id=${params.id}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /*
        Email for closure notifications
    */
    async getEmailsForClosureNotifications(settingId) {
        try {
            const {
                data: { emailsForClosureNotifications },
            } = await API.get(
                `/settings/email-notification?settingId=${settingId}`
            )
            return emailsForClosureNotifications
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createEmailForClosureNotifications(params) {
        try {
            const {
                data: { emailForClosureNotifications },
            } = await API.post(`/settings/email-notification`, params)
            return emailForClosureNotifications
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateEmailForClosureNotifications(params) {
        try {
            const { id, ...paramsToUpdate } = params
            const {
                data: { emailForClosureNotifications },
            } = await API.put(
                `/settings/email-notification/${id}`,
                paramsToUpdate
            )
            return emailForClosureNotifications
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteEmailForClosureNotifications(params) {
        try {
            const {
                data: { message },
            } = await API.delete(
                `/settings/email-notification?settingId=${params.settingId}&id=${params.id}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /**
     * BOM
     */
    async getBOMsChangelog({
        projectId,
        status,
        code,
        createdBy,
        deliver,
        startDate,
    }) {
        try {
            let query = ''
            if (createdBy) {
                query += `createdBy=${createdBy}`
            }
            if (code) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `code=${code}&`
            }
            if (projectId) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `projectId=${projectId}&`
            }
            if (status) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `status=${status}`
            }
            if (deliver) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `deliver=${deliver}`
            }
            if (startDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `startDate=${startDate}`
            }
            const {
                data: { boms },
            } = await API.get(`/bom/changelog?${query}`)
            return boms
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getBOMsChangelogForStock({ projectId }) {
        try {
            let query = ''
            if (projectId) {
                query += `projectId=${projectId}&`
            }
            const {
                data: { boms },
            } = await API.get(`/bom/changelog-for-stock?${query}`)
            return boms
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createBOM(params) {
        try {
            const {
                data: { boms },
            } = await API.post(`/bom`, params)
            return boms
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateBOM(params) {
        try {
            const {
                data: { boms },
            } = await API.put(`/bom`, params)
            return boms
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateBomNotes(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put(`/bom/notes`, params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async hideBomALert({ workOrderId, bomId }) {
        try {
            await API.put(
                `/bom/alert?workOrderId=${workOrderId}&bomId=${bomId}`
            )
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async approveBOM(params) {
        try {
            const { id, workOrderId } = params
            const {
                data: { bom },
            } = await API.put(`/bom/approve/${id}`, {
                workOrderId,
            })
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async closeBOM(params) {
        try {
            const { id, workOrderId } = params
            const {
                data: { bom },
            } = await API.put(`/bom/close/${id}`, { workOrderId })
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendPartials(params) {
        try {
            const { workOrderId, bomId, requests } = params
            const {
                data: { response },
            } = await API.put(`/bom/send-partials`, {
                workOrderId,
                bomId,
                requests,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendBOM(params) {
        try {
            const {
                workOrderId,
                bomId,
                partials,
                signCode,
                requestId,
                requestedFor,
                requestedOn,
                notificationId,
            } = params
            const {
                data: { response },
            } = await API.put(`/bom/send-bom`, {
                workOrderId,
                bomId,
                partials,
                signCode,
                requestId,
                requestedFor,
                requestedOn,
                notificationId,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteBOM(params) {
        try {
            const { id, workOrderId } = params
            const {
                data: { bom },
            } = await API.delete(`/bom?id=${id}&workOrderId=${workOrderId}`)
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async rejectBOM(params) {
        try {
            const { id, workOrderId, rejectedNotes, notificationId } = params
            const {
                data: { bom },
            } = await API.put(`/bom/reject/${id}`, {
                workOrderId,
                rejectedNotes,
                notificationId,
            })
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateBOMItem(params) {
        try {
            const {
                data: { bom },
            } = await API.put(`/bom/bom-item`, params)
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateBomItemFile(params) {
        try {
            const {
                data: { bom },
            } = await API.put('/bom/bom-item-file', params)
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProjectLogs(id) {
        try {
            const {
                data: { logs },
            } = await API.get(`/projects/logs?id=${id}`)
            return logs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendPurchaseToReceived(id, params) {
        try {
            const keys = [
                'dueDate',
                'deliveryLocation',
                'importation',
                'incoterm',
                'notes',
                'type',
                'status',
                'totalEntries',
            ]
            const { notificationId, ...paramsToUpdate } = params
            Object.keys(paramsToUpdate).forEach(key => {
                if (!keys.includes(key) || params[key] === undefined) {
                    delete paramsToUpdate[key]
                }
            })
            const { data: request } = await API.put(
                `/purchase/send-to-received?id=${id}&notificationId=${notificationId}`,
                paramsToUpdate
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createInform(params) {
        try {
            const {
                data: { inform },
            } = await API.post('/settings/create-inform', params)
            return inform
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getInformDetails(settingId) {
        try {
            const {
                data: { informs },
            } = await API.get(`/settings/inform-details?id=${settingId}`)
            return informs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateInform(params) {
        try {
            const {
                data: { inform },
            } = await API.put('/settings/update-inform', params)
            return inform
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async workOrderAttachments(params) {
        try {
            const {
                data: { workOrder },
            } = await API.put('/workOrder/attachments', params)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async cleanProductionFields(params) {
        try {
            const { data } = await API.put(
                '/workOrder/clean-production-fields',
                params
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getStockCodes() {
        try {
            const {
                data: { codes },
            } = await API.get('/stock/stock-codes')
            return codes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getApprovedRequests() {
        try {
            const {
                data: { approvedRequests },
            } = await API.get('/stock/approved-requests')
            return approvedRequests
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async rejectPurchaseRequest(id, params) {
        try {
            const { notificationId, ...paramsToUpdate } = params
            const { data: request } = await API.put(
                `/purchase/reject?id=${id}&notificationId=${notificationId}`,
                paramsToUpdate
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProgressSummary({
        status = 'approved',
        projectId,
        companyId,
    } = {}) {
        try {
            let queryParams = `status=${encodeURIComponent(status)}`
            if (projectId) {
                queryParams += `&projectId=${encodeURIComponent(projectId)}`
            }
            if (companyId) {
                queryParams += `&companyId=${encodeURIComponent(companyId)}`
            }
            const {
                data: { workOrders },
            } = await API.get(`/workOrder/progress-summary?${queryParams}`)
            return workOrders
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getWorkOrdersByProject({ projectId, includeClosed, companyId }) {
        try {
            return await collectWorkOrderPages(async (limit, offset) => {
                let queryParams = `projectId=${encodeURIComponent(
                    projectId
                )}&includeClosed=${includeClosed}&limit=${limit}&offset=${offset}`
                if (companyId) {
                    queryParams += `&companyId=${encodeURIComponent(companyId)}`
                }
                const {
                    data: { workOrders },
                } = await API.get(`/workOrder/find-by-project?${queryParams}`)
                return workOrders
            })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getLiteWorkOrdersByProject({ projectId, includeClosed, companyId }) {
        try {
            return await collectWorkOrderPages(async (limit, offset) => {
                let queryParams = `projectId=${encodeURIComponent(
                    projectId
                )}&includeClosed=${includeClosed}&lite=true&limit=${limit}&offset=${offset}`
                if (companyId) {
                    queryParams += `&companyId=${encodeURIComponent(companyId)}`
                }
                const {
                    data: { workOrders },
                } = await API.get(`/workOrder/find-by-project?${queryParams}`)
                return workOrders
            })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validateFinishWorkOrders(params) {
        try {
            const {
                data: { result },
            } = await API.post('/workOrder/validate-finish', {
                ...(params || {}),
            })
            return result
        } catch (error) {
            const errorResponse = error.response?.data
            throw new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
        }
    },
    async validateCloseWorkOrders(params) {
        try {
            const {
                data: { result },
            } = await API.post('/workOrder/validate-close', {
                ...(params || {}),
            })
            return result
        } catch (error) {
            const errorResponse = error.response?.data
            throw new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
        }
    },

    // Invoice settings
    async getInvoiceSettings(settingId) {
        try {
            const settings = await API.get(
                `/settings/invoice?settingId=${settingId}`
            )
            return settings
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    /*
        MACHINES
    */
    async getMachines(settingId) {
        try {
            const {
                data: { machines },
            } = await API.get(`/settings/machine/?settingId=${settingId}`)
            return machines
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getMachine(settingId, machineId) {
        try {
            const {
                data: { machine },
            } = await API.get(
                `/settings/machine/?settingId=${settingId}&machineId=${machineId}`
            )
            return machine
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createInvoiceSettingItem(params) {
        try {
            const { data } = await API.post(
                `/settings/invoice/${params.itemType}`,
                {
                    name: params.name,
                    description: params.description,
                    enabledValue: params.enabledValue,
                    settingId: params.settingId,
                }
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateInvoiceSettingItem(params) {
        try {
            const { data } = await API.put(
                `/settings/invoice/${params.itemType}/${params.itemId}`,
                {
                    ...(params.name ? { name: params.name } : {}),
                    ...(params.description
                        ? { description: params.description }
                        : {}),
                    ...(params.enabledValue
                        ? { enabledValue: params.enabledValue }
                        : { enabledValue: false }),
                    settingId: params.settingId,
                }
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteInvoiceSettingItem(params) {
        try {
            const response = await API.delete(
                `/settings/invoice/${params.itemType}?settingId=${params.settingId}&itemId=${params.itemId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createForeignTradeSupplier(params) {
        try {
            const { data } = await API.post(
                `/settings/invoice/foreignTradeSupplier`,
                {
                    settingId: params.settingId,
                    supplierId: params.supplierId,
                    name: params.name,
                }
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateForeignTradeSupplier(params) {
        try {
            const { data } = await API.put(
                `/settings/invoice/foreignTradeSupplier/${params.itemId}`,
                {
                    settingId: params.settingId,
                    supplierId: params.supplierId,
                    name: params.name,
                }
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createInvoice(params) {
        try {
            const {
                data: { invoice },
            } = await API.post(`/invoice`, params)
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getInvoices({ createdBy, projectId, status, startDate, endDate }) {
        try {
            let query = ''
            if (createdBy) {
                query += `createdBy=${createdBy}`
            }
            if (status) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `status=${status}`
            }
            if (projectId) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `projectId=${projectId}`
            }
            if (startDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `startDate=${startDate}`
            }
            if (endDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `endDate=${endDate}`
            }
            const {
                data: { invoices },
            } = await API.get(`/invoice?${query}`)
            return invoices
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getProjectInvoices(projectId) {
        try {
            const {
                data: { invoices },
            } = await API.get(
                `/invoice/project-invoices?projectId=${projectId}`
            )
            return invoices
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getCompletedInvoices(params = {}) {
        try {
            const {
                data: { invoices },
            } = await API.get('/invoice/completed', { params })
            return invoices
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async generateInvoice({
        id,
        projectId,
        attached,
        invoiceNumber,
        notificationId,
        settingId,
    }) {
        try {
            const {
                data: { invoice },
            } = await API.put(`/invoice/generate`, {
                id,
                projectId,
                attached,
                invoiceNumber,
                notificationId,
                settingId,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sentToGenerate({ id, projectId, notificationId }) {
        try {
            const {
                data: { invoice },
            } = await API.put(`/invoice/sentToGenerating`, {
                id,
                projectId,
                notificationId,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async approvalInvoice({ id, projectId, notificationId, settingId }) {
        try {
            const {
                data: { invoice },
            } = await API.put(`/invoice/approval`, {
                id,
                projectId,
                notificationId,
                settingId,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendInvoiceToClient({ id, projectId }) {
        try {
            const {
                data: { invoice },
            } = await API.put(`/invoice/send-to-client`, {
                id,
                projectId,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async invoiceAttachments({ id, projectId, files }) {
        try {
            const {
                data: { invoice },
            } = await API.put(`/invoice/attachments`, {
                id,
                projectId,
                files,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createMachine(params) {
        try {
            const {
                data: { machine },
            } = await API.post(`/settings/machine/`, params)
            return machine
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateMachine(params) {
        try {
            const { machineId, ...paramsToUpdate } = params
            const {
                data: { machine },
            } = await API.put(`/settings/machine/${machineId}`, paramsToUpdate)
            return machine
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteMachine(params) {
        try {
            const {
                data: { message },
            } = await API.delete(
                `/settings/machine/?settingId=${params.settingId}&machineId=${params.machineId}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async rejectInvoice({ id, projectId, notes, notificationId }) {
        try {
            const {
                data: { invoice },
            } = await API.put(`/invoice/reject`, {
                id,
                projectId,
                notes,
                notificationId,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateInvoice({
        id,
        concept,
        category,
        projectId,
        region,
        items,
        attachments,
        credit,
        notes = undefined,
        amount,
        notificationId,
        warehouseReceipt,
    }) {
        try {
            const {
                data: { invoice },
            } = await API.put(`/invoice`, {
                id,
                concept,
                category,
                projectId,
                region,
                items,
                credit,
                amount,
                notificationId,
                warehouseReceipt,
                ...(attachments ? { attachments } : {}),
                ...(notes ? { notes } : {}),
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async rejectBomAfterApproval({ workOrderId, id, rejectedNotes }) {
        try {
            const {
                data: { bom },
            } = await API.put(`/bom/reject-bom-after-approval/${id}`, {
                workOrderId,
                rejectedNotes,
            })
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async rejectBOMPartials({
        id,
        itemCode,
        workOrderId,
        rejectedNotes,
        requestId,
        showAlert,
    }) {
        try {
            const {
                data: { bom },
            } = await API.put(`/bom/reject-partials`, {
                id,
                itemCode,
                workOrderId,
                rejectedNotes,
                requestId,
                showAlert,
            })
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateNotification(notificationId, readStatus) {
        try {
            const response = await API.put(
                `/users/notifications/${notificationId}`,
                { read: readStatus }
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteNotification(notificationId) {
        try {
            const {
                data: { message },
            } = await API.delete(`/users/notifications/${notificationId}`)
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async readAllNotifications() {
        try {
            const response = await API.put(`/users/notifications/readAll`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteAllNotifications() {
        try {
            const response = await API.delete(`/users/notifications/deleteAll`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async searchOperatorByCode(companyId, code) {
        try {
            const {
                data: { operator },
            } = await API.get(
                `/settings/operators/search-by-code?companyId=${companyId}&code=${code}`
            )
            return operator
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async findWorkOrderByCode(companyId, operatorCode, code) {
        try {
            const {
                data: { workOrder },
            } = await API.get(
                `/find/find-by-code?companyId=${companyId}&operatorCode=${operatorCode}&code=${code}`
            )
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async findBOMByCode(code, id) {
        try {
            const {
                data: { bom },
            } = await API.get(
                `/bom/find-by-code?code=${!id ? code : `${code}&`}${
                    id ? `id=${id}` : ''
                }`
            )
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async findWOByCode(code, companyId) {
        try {
            const url = companyId
                ? `/workOrder/find-by-code?code=${code}&companyId=${companyId}`
                : `/workOrder/find-by-code?code=${code}`
            const {
                data: { workOrder },
            } = await API.get(url)
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getLiteCompany(companyId) {
        try {
            const {
                data: { company },
            } = await API.get(`/company/lite/${companyId}`)
            return company
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async validateReCaptcha(token) {
        try {
            const {
                data: { response },
            } = await API.post('/company/validate-token', { token })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getChangelogInvoices({ startDate, endDate }) {
        try {
            const {
                data: { invoices },
            } = await API.get(
                `/invoice/changelog?startDate=${startDate}&endDate=${endDate}`
            )
            return invoices
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getInformInvoices({ startDate, endDate }) {
        try {
            const {
                data: { invoices },
            } = await API.get(
                `/invoice/inform?startDate=${startDate}&endDate=${endDate}`
            )
            return invoices
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async requestCreditNote(params) {
        try {
            const {
                data: { invoice },
            } = await API.put('/invoice/request-credit-note', params)
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async uploadCreditNote(params) {
        const { projectId, id, credit } = params
        try {
            const {
                data: { invoice },
            } = await API.put('/invoice/upload-credit-note', {
                projectId,
                id,
                credit,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async generateCreditNote(params) {
        const {
            projectId,
            id,
            creditPrice,
            creditRequests,
            conceptType,
        } = params
        try {
            const {
                data: { invoice },
            } = await API.put('/invoice/generate-credit-note', {
                projectId,
                id,
                creditPrice,
                creditRequests,
                conceptType,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async closeInvoice(params) {
        const { projectId, id } = params
        try {
            const {
                data: { invoice },
            } = await API.put('/invoice/close', {
                projectId,
                id,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getInvoice(number) {
        try {
            const {
                data: { invoice },
            } = await API.get(`/invoice/${number}`)
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async checkAlertNotification(params) {
        try {
            const {
                data: { notification },
            } = await API.put(`/notification/remove`, params)
            return notification
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async eraseNotifications() {
        try {
            await API.put(`/notification/erase`)
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPurchasesInform({ startDate, endDate }) {
        try {
            const {
                data: { result },
            } = await API.get(
                `/purchase/statistics?startDate=${startDate}&endDate=${endDate}`
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getInformPurchases({ startDate, endDate, signal } = {}) {
        try {
            const config = {}
            if (signal) {
                config.signal = signal
            }

            const {
                data: { purchases },
            } = await API.get(
                `/purchase/inform?startDate=${startDate}&endDate=${endDate}`,
                config
            )
            return purchases
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                throw error
            }
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getEngUserReports({ startDate, endDate }) {
        try {
            let query = ''
            if (startDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `startDate=${startDate}`
            }
            if (endDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `endDate=${endDate}`
            }
            const response = await API.get(`/cost/getEngUserReports?${query}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getPurchasesStatusInform({ informDate }) {
        try {
            const {
                data: { statusInform },
            } = await API.get(
                `/purchase/status-inform?informDate=${informDate}`
            )
            return statusInform
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteInvoice({ id, projectId }) {
        try {
            const {
                data: { invoice },
            } = await API.delete(`/invoice?id=${id}&projectId=${projectId}`)
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePurchaseFiles(id, params) {
        try {
            const {
                data: { purchase },
            } = await API.put(`/purchase/files?id=${id}`, params)
            return purchase
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteInform(settingId, informId) {
        try {
            const {
                data: { inform },
            } = await API.delete(
                `/settings/delete-inform?settingId=${settingId}&informId=${informId}`
            )
            return inform
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createProformaFeature(params) {
        try {
            const {
                data: { invoice },
            } = await API.post(`/invoice/proforma-features`, params)
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateProformaFeature(id, params) {
        try {
            const {
                name,
                projectId,
                address,
                phone,
                proformaValue,
                tariffItem,
                status,
                description,
                notificationId,
            } = params
            const {
                data: { invoice },
            } = await API.put(`/invoice/proforma-features?id=${id}`, {
                name,
                projectId,
                address,
                phone,
                proformaValue,
                tariffItem,
                status,
                description,
                notificationId,
            })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getCanceledWorkOrders(params) {
        try {
            const { startDate, endDate } = params
            const {
                data: { workOrders },
            } = await API.get(
                `/workOrder/canceled?${
                    startDate ? `startDate=${startDate}&` : ''
                }${endDate ? `endDate=${endDate}` : ''}`
            )
            return workOrders
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getAllPurchases({ projectId, status, signal } = {}) {
        try {
            const config = {}
            if (signal) {
                config.signal = signal
            }

            const {
                data: { purchases },
            } = await API.get(
                `/purchase/all?${projectId ? `projectId=${projectId}` : ''}&${
                    status ? `status=${status}` : ''
                }`,
                config
            )
            return purchases
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                throw error
            }
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteEntry({ purchaseId, itemId, id }) {
        try {
            const {
                data: { entry },
            } = await API.delete(
                `/purchase/entries?purchaseId=${purchaseId}&itemId=${itemId}&id=${id}`
            )
            return entry
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getBOMPartialDeliveries() {
        try {
            const {
                data: { partialDeliveries },
            } = await API.get(`/bom/partial-deliveries`)
            return partialDeliveries
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getBOMById({ bomId, companyId }) {
        try {
            let queryParams = `id=${bomId}`
            if (companyId) {
                queryParams += `&companyId=${companyId}`
            }
            const {
                data: { bom },
            } = await API.get(`/bom/find-by-id?${queryParams}`)
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async restoreInvoice({ id, projectId }) {
        try {
            const {
                data: { invoice },
            } = await API.put(`/invoice/restore-invoice`, { id, projectId })
            return invoice
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async purchaseIndicators(params) {
        try {
            const { start, end } = params
            const {
                data: { inform },
            } = await API.get(`/purchase/indicators?start=${start}&end=${end}`)
            return inform
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createIndicators(params = {}) {
        try {
            const { data } = await API.post(
                `/purchase/createIndicators`,
                params,
                { timeout: 900000 }
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data
            throw new Error(
                errorResponse?.message ||
                    errorResponse?.error ||
                    error.message ||
                    'Failed to create indicators'
            )
        }
    },
    async reconcileIndicators(params = {}) {
        try {
            const { month, from, to } = params
            const qs = month
                ? `month=${month}`
                : `from=${from || '2025-07'}&to=${to || '2025-12'}`
            const { data } = await API.get(
                `/purchase/reconcileIndicators?${qs}`,
                {
                    timeout: 900000,
                }
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data
            throw new Error(
                errorResponse?.message ||
                    errorResponse?.error ||
                    error.message ||
                    'Failed to reconcile indicators'
            )
        }
    },
    async getReportIndicators(params) {
        try {
            const { month } = params
            const {
                data: { report },
            } = await API.get(`/purchase/indicatorsReport?month=${month}`, {
                timeout: 300000,
            })
            return report
        } catch (error) {
            const errorResponse = error.response?.data
            throw new Error(
                errorResponse?.message ||
                    errorResponse?.error ||
                    error.message ||
                    'Failed to generate indicators report'
            )
        }
    },

    async closeBOMPartials({ id, itemCode, workOrderId, requestId }) {
        try {
            const {
                data: { bom },
            } = await API.put(`/bom/close-partial-delivery`, {
                id,
                itemCode,
                workOrderId,
                requestId,
            })
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async requestCloseBOMPartials({
        id,
        itemCode,
        workOrderId,
        notes,
        requestId,
    }) {
        try {
            const {
                data: { bom },
            } = await API.put(`/bom/request-to-close-partial-delivery`, {
                id,
                itemCode,
                workOrderId,
                notes,
                requestId,
            })
            return bom
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    // Packing list
    async createPackingList(pl) {
        try {
            const {
                projectId,
                requestedShipmentDate,
                scheduledFor,
                deliveryInformation,
                notes,
                packages,
                attachments,
                selectedWorkOrders,
                selectedBOMs,
                useSuggested,
                container,
                airShipment,
                blockActionsFromPMs,
            } = pl
            const {
                data: { packingList },
            } = await API.post('/packingList', {
                projectId,
                requestedShipmentDate,
                scheduledFor,
                deliveryInformation,
                notes,
                packages,
                attachments,
                selectedWorkOrders,
                selectedBOMs,
                useSuggested,
                container,
                airShipment,
                blockActionsFromPMs,
            })
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async importPackages(params) {
        try {
            const {
                data: { packingList, createdNewPackingList },
            } = await API.put('/packingList/import-packages', params)
            return { packingList, createdNewPackingList }
        } catch (error) {
            if ((error.response && error.response?.data) || {}) {
                const customError = new Error(
                    error.response?.data ||
                        {}.message ||
                        error.response?.data ||
                        {}.error
                )
                customError.response = error.response
                throw customError
            }
            throw error
        }
    },

    async createPackage(params) {
        try {
            const { data } = await API.put(
                '/packingList/create-package',
                params
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data
            const customError = new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
            if (error.response) {
                customError.status = error.response.status
                customError.response = error.response
            }
            throw customError
        }
    },

    async deletePackage(params) {
        try {
            const {
                data: { packages },
            } = await API.put('/packingList/delete-package', params)
            return packages
        } catch (error) {
            const errorResponse = error.response?.data
            const customError = new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
            if (error.response) {
                customError.status = error.response.status
                customError.response = error.response
            }
            throw customError
        }
    },

    async updatePackage(params) {
        try {
            const {
                data: { packages },
            } = await API.put('/packingList/update-package', params)
            return packages
        } catch (error) {
            const errorResponse = error.response?.data
            const customError = new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
            if (error.response) {
                customError.status = error.response.status
                customError.response = error.response
            }
            throw customError
        }
    },

    async updatePackageParams({ projectId, packingListId, packageId, params }) {
        try {
            const {
                data: { packageElement },
            } = await API.put(
                `/packingList/package-params?projectId=${projectId}&packingListId=${packingListId}&packageId=${packageId}`,
                params
            )
            return packageElement
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePackageGrossMass({
        projectId,
        packingListId,
        packageId,
        grossMass,
    }) {
        try {
            const {
                data: { packages },
            } = await API.put(
                `/packingList/package-gross-mass?projectId=${projectId}&packingListId=${packingListId}&packageId=${packageId}`,
                { grossMass }
            )
            return packages
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updatePackageLock({ packingListId, packageId, lockStatus }) {
        try {
            const { data } = await API.put('/packingList/update-package-lock', {
                packingListId,
                packageId,
                lockStatus,
            })
            return data
        } catch (error) {
            const errorResponse = error.response?.data
            const customError = new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
            if (error.response) {
                customError.status = error.response.status
                customError.response = error.response
            }
            throw customError
        }
    },

    async markPLAspending(params) {
        try {
            const {
                data: { packingList },
            } = await API.put('/packingList/pending', params)
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async markPLAsScheduled(params) {
        try {
            const {
                data: { updatedPackingList, newPackingList },
            } = await API.put('/packingList/scheduled', params)
            return { updatedPackingList, newPackingList }
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async markPLAsReferred(params) {
        try {
            const {
                data: { packingList },
            } = await API.put('/packingList/referred', params)
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async markPLAsShipped(params) {
        try {
            const {
                data: { packingList },
            } = await API.put('/packingList/shipped', params)
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async markPLAsCompleted(params) {
        try {
            const {
                data: { packingList },
            } = await API.put('/packingList/completed', params)
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async rejectPackingList(params) {
        try {
            const {
                data: { packingList },
            } = await API.put('/packingList/reject', params)
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePackingList(params) {
        try {
            const {
                data: { packingList },
            } = await API.put('/packingList/', params)
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateBlockActionsFromPms(params) {
        try {
            const {
                data: { packingList },
            } = await API.put('/packingList/block-actions-from-pms', params)
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPackingLists({
        createdBy,
        projectId,
        status,
        startDate,
        endDate,
        id,
    }) {
        try {
            let query = ''
            if (createdBy) {
                query += `createdBy=${createdBy}`
            }
            if (status) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `status=${status}`
            }
            if (id) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `id=${id}`
            }
            if (projectId) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `projectId=${projectId}`
            }
            if (startDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `startDate=${startDate}`
            }
            if (endDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `endDate=${endDate}`
            }
            const {
                data: { packingLists },
            } = await API.get(`/packingList?${query}`)
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getShippedPackingListsReport({ startDate, endDate }) {
        try {
            const {
                data: { report },
            } = await API.get(
                `/packingList/shipped-packing-lists-report?startDate=${startDate}&endDate=${endDate}`
            )
            return { report }
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPackingListsForExportations({
        createdBy,
        projectId,
        startDate,
        endDate,
        id,
        ids,
    }) {
        try {
            let query = ''
            if (createdBy) {
                query += `createdBy=${createdBy}`
            }
            if (ids?.length) {
                const idsParam = Array.isArray(ids) ? ids.join(',') : ids
                if (query.length > 0) {
                    query += '&'
                }
                query += `ids=${encodeURIComponent(idsParam)}`
            }
            if (id) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `id=${id}`
            }
            if (projectId) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `projectId=${projectId}`
            }
            if (startDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `startDate=${startDate}`
            }
            if (endDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `endDate=${endDate}`
            }
            const {
                data: { packingLists },
            } = await API.get(`/packingList/forExportations?${query}`)
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async bulkGetPackingListsForExportations({
        ids,
        lite = false,
        enrichProjects = true,
    }) {
        try {
            const {
                data: { packingLists },
            } = await API.post('/packingList/forExportations/bulk', {
                ids,
                lite,
                enrichProjects,
            })
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getShippedPackingLists({ startDate, endDate }) {
        try {
            const {
                data: { packingLists },
            } = await API.get(
                `/packingList/shipped?startDate=${startDate}&endDate=${endDate}`
            )
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getESMStockPackingLists({
        viewerProjectId,
        availableToSend = false,
        ticketNameFile,
        packingListIds,
        limit,
        includeTrackingDetails = false,
    } = {}) {
        try {
            const params = {}
            if (viewerProjectId) {
                params.viewerProjectId = viewerProjectId
            }
            if (availableToSend) {
                params.availableToSend = true
            }
            if (ticketNameFile) {
                params.ticketNameFile = ticketNameFile
            }
            if (Array.isArray(packingListIds) && packingListIds.length) {
                params.packingListIds = packingListIds.join(',')
            }
            if (limit) {
                params.limit = limit
            }
            if (includeTrackingDetails) {
                params.includeTrackingDetails = true
            }
            const { data } = await API.get(`/packingList/esm-stock`, {
                params,
                timeout: 60000,
            })
            return data
        } catch (error) {
            const errorResponse = error.response?.data
            throw new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
        }
    },

    async getESMWorkOrderId(code) {
        try {
            const { data } = await API.get(`/packingList/esm-work-order-id`, {
                params: { code },
                timeout: 30000,
            })
            return data
        } catch (error) {
            const errorResponse = error.response?.data
            throw new Error(
                errorResponse?.message || errorResponse?.error || error.message
            )
        }
    },

    async recordPackingItemReceipt(params) {
        try {
            const {
                data: { packingList },
            } = await API.put(`/packingList/record-receipt`, params)
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async sendPackingItemsToSite(params) {
        try {
            const { data } = await API.put(`/packingList/send-to-site`, params)
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async uploadSignedDeliveryTicket(params) {
        try {
            const {
                data: { packingLists },
            } = await API.put(
                `/packingList/upload-signed-delivery-ticket`,
                params
            )
            return packingLists || []
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getAllPackingLists({ projectId, startDate, endDate }) {
        try {
            let query = ''
            if (projectId) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `projectId=${projectId}`
            }
            if (startDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `startDate=${startDate}`
            }
            if (endDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `endDate=${endDate}`
            }
            const {
                data: { packingLists },
            } = await API.get(`/packingList/all?${query}`)
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPackingListsForBalanceProject({ projectId, startDate, endDate }) {
        try {
            let query = ''
            if (projectId) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `projectId=${projectId}`
            }
            if (startDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `startDate=${startDate}`
            }
            if (endDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `endDate=${endDate}`
            }
            const {
                data: { packingLists },
            } = await API.get(`/packingList/for-balance-project?${query}`)
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getCalendarPackingLists({ startDate, endDate }) {
        try {
            let query = ''
            if (startDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `startDate=${startDate}`
            }
            if (endDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `endDate=${endDate}`
            }
            const {
                data: { packingLists },
            } = await API.get(`/packingList/calendar?${query}`)
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getDelayedPackingLists() {
        try {
            const {
                data: { packingLists },
            } = await API.get(`/packingList/delayed`)
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPackingListsByProject({ projectId, startDate }) {
        try {
            let query = ''
            if (projectId) {
                query += `projectId=${projectId}`
            }
            if (startDate) {
                query += `&startDate=${startDate}`
            }
            const {
                data: { packingLists },
            } = await API.get(`/packingList/by-project?${query}`)
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getWOPackingListsLite() {
        try {
            const {
                data: { packingLists },
            } = await API.get(`/packingList/lite`)
            return packingLists
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deletePackingList({ id, projectId }) {
        try {
            const {
                data: { packingList },
            } = await API.delete(`/packingList?id=${id}&projectId=${projectId}`)
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async packingListAttachments({ id, projectId, files }) {
        try {
            const {
                data: { packingList },
            } = await API.put(`/packingList/attachments`, {
                id,
                projectId,
                files,
            })
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async packingListRemissions({ id, projectId, files }) {
        try {
            const {
                data: { packingList },
            } = await API.put(`/packingList/remissions`, {
                id,
                projectId,
                files,
            })
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async packingListReceiptConfirmations({ id, projectId, files }) {
        try {
            const {
                data: { packingList },
            } = await API.put(`/packingList/receiptConfirmations`, {
                id,
                projectId,
                files,
            })
            return packingList
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getBOMPartialDeliveriesForReport({ startDate, endDate }) {
        try {
            const {
                data: { partialDeliveries },
            } = await API.get(
                `/bom/partial-deliveries-for-report?startDate=${startDate}&endDate=${endDate}`
            )
            return partialDeliveries
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async purchaseReadyForPricing(id, params) {
        try {
            const { notificationId, ...paramsToUpdate } = params
            const {
                data: { request },
            } = await API.put(
                `/purchase/ready-for-pricing?id=${id}&notificationId=${notificationId}`,
                paramsToUpdate
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createLead(lead, notificationId) {
        try {
            const {
                companyId,
                userId,
                clientId,
                clientName,
                preconManagerId,
                collaborators,
                notes,
                currency,
                exchange,
                files,
                name,
                country,
                state,
                city,
                tags,
                contacts,
                salesRepIds,
                salesRepContactIds,
                requestDate,
                invitationDate,
                changeOrderRequest,
                qtyOrders,
            } = lead
            const response = await API.post('/leads', {
                notificationId,
                companyId,
                userId,
                clientId,
                clientName,
                collaborators,
                currency,
                exchange,
                preconManagerId,
                files,
                status: 'inProgress',
                name,
                notes,
                country,
                state,
                city,
                tags,
                contacts,
                salesRepIds,
                salesRepContactIds,
                requestDate,
                invitationDate,
                changeOrderRequest,
                qtyOrders,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createCostsDetails({ leadId, cost }) {
        try {
            const {
                data: { response },
            } = await API.put('/leads/createCosts', {
                leadId: leadId,
                ...cost,
            })

            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateCostsDetails({ leadId, costId, costDiff }) {
        try {
            const {
                data: { response },
            } = await API.put('/leads/updateCosts', {
                leadId: leadId,
                costId,
                ...costDiff,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteLeadScope(params) {
        try {
            const {
                data: { response },
            } = await API.put('/leads/deleteLeadScope', params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async importLeadsCosts(params) {
        try {
            const {
                data: { costs },
            } = await API.post(`leads/import`, params)
            return costs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateLead(lead) {
        try {
            const {
                companyId,
                accountManager,
                preconManagerId,
                userId,
                collaborators,
                clientId,
                clientName,
                notes,
                currency,
                exchange,
                name,
                country,
                state,
                city,
                status,
                tags,
                contacts,
                salesRepIds,
                salesRepContactIds,
                requestDate,
                invitationDate,
                id,
                changeOrderRequest,
            } = lead
            const {
                data: { lead: response },
            } = await API.put('/leads', {
                companyId,
                accountManager,
                preconManagerId,
                userId,
                clientId,
                clientName,
                currency,
                exchange,
                collaborators,
                status,
                name,
                notes,
                country,
                state,
                city,
                tags,
                contacts,
                salesRepIds,
                salesRepContactIds,
                requestDate,
                invitationDate,
                id,
                changeOrderRequest,
            })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async moveLeadFiles(params) {
        try {
            const { id, sourceFolder, destinationFolder } = params
            return API.put('/leads/move-files', {
                id,
                sourceFolder,
                destinationFolder,
            })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async approveLead({ id }) {
        try {
            const {
                data: { lead },
            } = await API.put(`/leads/approve?id=${id}`)
            return lead
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async restoreLead(lead) {
        try {
            const response = await API.put(`/leads/restoreLead`, lead)
            return response.data.lead
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async rejectLead({ id, rejectionReason, notificationMessage, contacts }) {
        try {
            const {
                data: { lead },
            } = await API.put(`/leads/reject?id=${id}`, {
                rejectionReason,
                notificationMessage,
                contacts,
            })
            return lead
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async onHoldLead({ id, notificationMessage, contacts }) {
        try {
            const {
                data: { lead },
            } = await API.put(`/leads/onHold?id=${id}`, {
                notificationMessage,
                contacts,
            })
            return lead
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteLead({ id }) {
        try {
            const {
                data: { lead },
            } = await API.delete(`/leads/delete?id=${id}`)
            return lead
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getLeads(params) {
        try {
            const {
                data: { leads },
            } = await API.get(`/leads/?status=${params.status}`)
            return leads
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateLeadFiles(params) {
        try {
            const response = await API.put(`/leads/updateLeadFiles`, { params })
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getRejectedLeads({ startDate, endDate }) {
        try {
            let query = ''
            if (startDate) {
                query += `startDate=${startDate}`
            }
            if (endDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `endDate=${endDate}`
            }
            const {
                data: { leads },
            } = await API.get(`/leads/rejected?${query}`)
            return leads
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getApprovedLeads() {
        try {
            const {
                data: { leads },
            } = await API.get(`/leads/approved`)
            return leads
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async approveAlternate({ quoteId, alternateId }) {
        try {
            const {
                data: { cost },
            } = await API.put(`/cost/approve-alternate`, {
                quoteId,
                alternateId,
            })
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async backToIntransit({ id, notificationId, backToInTransitNotes }) {
        try {
            const {
                data: { request },
            } = await API.put(`/purchase/back-to-in-transit`, {
                id,
                notificationId,
                backToInTransitNotes,
            })
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getChatsInFollowUp() {
        try {
            const {
                data: { chats },
            } = await API.get(`/chat/follow-up`)
            return chats
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async multipleProductionRegister(params) {
        try {
            const {
                data: { items },
            } = await API.put(`/workOrder/multiple-production-register`, params)
            return items
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateDeliveryTemporalQuantity({ workOrderId, delivery }) {
        try {
            const {
                data: { newDeliverie },
            } = await API.put('/workOrder/delivery-temporal-quantity', {
                workOrderId: workOrderId,
                delivery: delivery,
            })
            return newDeliverie
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    // Forecast
    async createBulkForecast({ projectId, entries }) {
        try {
            const response = await API.post('/forecast/bulk-forecast', {
                projectId,
                entries,
            })
            return response.data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateBulkForecast({ projectId, entries }) {
        try {
            const response = await API.put('/forecast/bulk-forecast', {
                projectId,
                entries,
            })
            return response.data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateInitialProjection({ projectId, entries }) {
        try {
            const response = await API.put('/forecast/initial-projection', {
                projectId,
                entries,
            })
            return response.data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    //Estimating plan
    async getQuotesEstimatingPlan(status) {
        try {
            const response = await API.get(
                `/quotes/estimatingQuotes?status=${status}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getSimpleQuotesByStatus(status) {
        try {
            const response = await API.get(
                `/quotes/getSimpleQuotesbyStatus?status=${status}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getClientApprovedQuotes() {
        try {
            const {
                data: { quotes },
            } = await API.get(`/quotes/client-approved`)
            return quotes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateClientDocumentValidation({
        clientId,
        fileType,
        approved,
        rejectionNotes = '',
        quoteId,
    }) {
        try {
            const {
                data: { client, document },
            } = await API.put('/clients/document-validation', {
                clientId,
                fileType,
                approved,
                rejectionNotes,
                ...(quoteId ? { quoteId } : {}),
            })
            return { client, document }
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateSapCheck({ quoteId, field, value }) {
        try {
            const {
                data: { quote },
            } = await API.put(`/quotes/${quoteId}/sap-check`, {
                field,
                value,
            })
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateSapData({ quoteId, offer, sapCode }) {
        try {
            const {
                data: { quote },
            } = await API.put(`/quotes/${quoteId}/sap-data`, {
                offer,
                sapCode,
            })
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getCostsBigQ(status) {
        try {
            const response = await API.get(
                `/cost/estimating-plan?status=${status}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getLiteCostsBigQ(startDate, endDate) {
        try {
            const response = await API.get(
                `/cost/getLiteCostsBigQ?startDate=${startDate}&endDate=${endDate}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getEngineerCosts(startDate, endDate) {
        try {
            const response = await API.get(
                `/cost/getEngineerCosts?startDate=${startDate}&endDate=${endDate}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getForecastCosts(startDate, endDate, ids) {
        try {
            const response = await API.get(
                `/cost/getForecastCosts?startDate=${startDate}&endDate=${endDate}&ids=${ids}`
            )
            return response.data.costs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getPreDesignsHistory({ startDate, endDate }) {
        try {
            const response = await API.get(
                `/cost/predesign-history?startDate=${startDate}&endDate=${endDate}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getGeneratedPreDesigns({ startDate, endDate }) {
        try {
            let query = ''
            if (startDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `startDate=${startDate}`
            }
            if (endDate) {
                if (query.length > 0) {
                    query += '&'
                }
                query += `endDate=${endDate}`
            }
            const response = await API.get(
                `/cost/getGeneratedPreDesigns?${query}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async forEngineering(quoteId, costId, item) {
        try {
            const {
                data: { cost },
            } = await API.put(
                `cost/forEngineering?quoteId=${quoteId}&costId=${costId}`,
                {
                    ...item,
                }
            )
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async returnEngineering(quoteId, costId, item) {
        try {
            const {
                data: { cost },
            } = await API.put(
                `cost/returnEngineering?quoteId=${quoteId}&costId=${costId}`,
                {
                    ...item,
                }
            )
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async rejectPreDesign(quoteId, costId, item) {
        try {
            const {
                data: { cost },
            } = await API.put(
                `cost/rejectPreDesign?quoteId=${quoteId}&costId=${costId}`,
                {
                    ...item,
                }
            )
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async changeEngDueDate(quoteId, costId, item) {
        try {
            const {
                data: { cost },
            } = await API.put(
                `cost/changeEngDueDate?quoteId=${quoteId}&costId=${costId}`,
                {
                    ...item,
                }
            )
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async setEngineer(quoteId, costId, item) {
        try {
            const {
                data: { cost },
            } = await API.put(
                `cost/setEngineer?quoteId=${quoteId}&costId=${costId}`,
                {
                    ...item,
                }
            )
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validateInvoicesByProject(projectId) {
        try {
            const {
                data: { response },
            } = await API.get(
                `/invoice/validate-by-project?projectId=${projectId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validateReservesByProject(projectId) {
        try {
            const {
                data: { response },
            } = await API.get(
                `/stock/validate-by-project?projectId=${projectId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validatePurchasesByProject(projectId) {
        try {
            const {
                data: { response },
            } = await API.get(
                `/purchase/validate-by-project?projectId=${projectId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validateWorkOrderAndPackingList(projectId) {
        try {
            const {
                data: { response },
            } = await API.get(
                `/workOrder/validate-by-project?projectId=${projectId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendProjectToTheClosingProcess(projectId, params) {
        try {
            const {
                data: { project },
            } = await API.put(
                `/projects/send-to-closing?id=${projectId}`,
                params
            )
            return project
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async closingProjects() {
        try {
            const {
                data: { projects },
            } = await API.get(`/projects/closing`)
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async restoreWorkOrder(id, code) {
        try {
            const {
                data: { order },
            } = await API.put(`/workOrder/restore?id=${id}&code=${code}`)
            return order
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async returnWorkOrderToApproved(params) {
        try {
            const { workOrderId } = params
            const {
                data: { workOrder },
            } = await API.put(`/workOrder/return-to-approved`, {
                workOrderId,
            })
            return workOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateChatFromQuote(params) {
        try {
            const {
                data: { chat },
            } = await API.put(`/quotes/update-chat`, params)
            return chat
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateSpecialMaterial(params) {
        try {
            const { materialId, ...paramsToUpdate } = params
            const {
                data: { material },
            } = await API.put(
                `/settings/material/${materialId}`,
                paramsToUpdate
            )
            return material
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createSpecialMaterial(params) {
        try {
            const {
                data: { response },
            } = await API.post(`/settings/material/`, params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteSpecialMaterial(params) {
        try {
            const {
                data: { response },
            } = await API.put(
                `/settings/delete-material/?settingId=${params.settingId}&materialId=${params.materialId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async checkParamsForClosing(params) {
        try {
            const {
                data: { project },
            } = await API.put(`/projects/check-params-for-closing`, params)
            return project
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async requestQuotes(params) {
        try {
            const {
                data: { quote },
            } = await API.post(`/purchase/multipleQuotes`, params)
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async reSendPurchaseQuoteEmail(params) {
        try {
            const {
                data: { quote },
            } = await API.post(`/purchase/reSendPurchaseQuoteEmail`, params)
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async verifyEmail(email, resend = false) {
        try {
            const {
                data: { user },
            } = await API.get(
                `/authentication/verify-email?email=${email}&resend=${resend}`
            )
            return user
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw errorResponse
        }
    },

    async getPurchaseQuotes(params) {
        try {
            const {
                data: { purchaseQuotes },
            } = await API.get(
                `/purchaseQuotes/getPurchaseQuotes/?params=${params}`
            )
            return purchaseQuotes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            const err = new Error(errorResponse.message || errorResponse.error)
            if (errorResponse.language) {
                err.language = errorResponse.language
            }
            throw err
        }
    },

    async uploadPurchaseQuote(params) {
        try {
            const API2 = axios.create({
                baseURL: process.env.VUE_APP_API_HOST,
            })
            const token = auth().currentUser
                ? await auth().currentUser.getIdToken()
                : ''
            API2.interceptors.request.use(
                async function(config) {
                    config.headers.common.Authorization = `Bearer ${token}`
                    config.headers.common['Access-Control-Allow-Origin'] = '*'
                    config.headers.common['Access-Control-Allow-Methods'] =
                        'GET, POST, PUT, DELETE'
                    config.headers.common['Access-Control-Allow-Headers'] =
                        'Content-Type'
                    config.headers.common['cache-control'] = 'no-cache'
                    return config
                },
                function(error) {
                    return Promise.reject(error)
                }
            )
            const {
                data: { purchaseQuotes },
            } = await API2.post(
                `/purchaseQuotes/uploadPurchaseQuote/?params=${params.params}`,
                {
                    paramsToUpdate: params.paramsToUpdate,
                    quotes: params.quotes,
                }
            )
            return purchaseQuotes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async increasePurchaseQuoteCounter(params) {
        try {
            const {
                data: { purchaseQuotes },
            } = await API.put(
                `/purchaseQuotes/increasePurchaseQuoteCounter/?params=${params}`
            )
            return purchaseQuotes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async uploadPurchaseQuoteFile(paramsId, formData) {
        try {
            const {
                data: { purchase },
            } = await API.post(
                `/purchaseQuotes/uploadPurchaseQuoteFile/?params=${paramsId}`,
                formData,
                {
                    headers: {
                        'content-type': 'multipart/form-data',
                    },
                }
            )

            return purchase
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async verifyCode(email, code) {
        try {
            const {
                data: { user },
            } = await API.get(
                `/authentication/verify-code?email=${email}&code=${code}`
            )
            return user
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async userProcesses(userId) {
        try {
            const {
                data: { processes },
            } = await API.get(`/users/user-processes?userId=${userId}`)
            return processes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async disableUserNotProjectManager(userId) {
        try {
            const response = await API.put(
                `/users/disable-user-not-project-manager`,
                { userId }
            )
            return response.data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async replaceAndDeleteUser(userId, sections) {
        try {
            await API.put(`/users/replace-and-delete`, { userId, sections })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async replaceAndDisableUser(userId, sections) {
        try {
            await API.put(`/users/replace-and-disable`, { userId, sections })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async closePurchaseRequest(params) {
        try {
            const { notificationId, ...paramsToUpdate } = params
            const { data: request } = await API.put(
                `/purchase/close?notificationId=${notificationId}`,
                paramsToUpdate
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    /** One-off: close Componenti readyToClose purchases via API (Admin). */
    async closeComponentiReadyToClosePurchases() {
        try {
            const {
                data: { result },
            } = await API.post('/purchase/close-componenti-ready-to-close')
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async closePurchaseByComex(params) {
        try {
            const { notificationId, ...paramsToUpdate } = params
            const { data: request } = await API.put(
                `/purchase/closeByComex?notificationId=${notificationId}`,
                paramsToUpdate
            )
            return request
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async addPreDesign(params) {
        try {
            const { quoteId, costId, preDesign } = params
            const {
                data: { cost },
            } = await API.put(
                `/cost/add-preDesign?quoteId=${quoteId}&costId=${costId}`,
                { preDesign }
            )
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async loadPreDesigns(quoteId) {
        try {
            const {
                data: { cost },
            } = await API.put(`/quotes/loadPreDesigns/${quoteId}`)
            return cost
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async addBacklog(backlog) {
        try {
            const response = await API.post(`/projects/add-backlog`, backlog)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getBacklog() {
        try {
            const response = await API.get(`/projects/get-backlog`)
            const backlog = response?.data?.backlog?.[0]
            return backlog || []
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getBacklogFile() {
        try {
            const response = await API.get(`/projects/get-backlogFile`)
            const backlogFile = response.data
            return backlogFile || []
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteBacklog(id) {
        try {
            const response = await API.delete(`/projects/delete-backlog/${id}`)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getChats(params = {}) {
        try {
            const {
                data: { chats },
            } = await API.get(
                `/chat${params.status ? `?status=${params.status}` : ''}`
            )
            return chats
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getQuoteProductTypes(settingId) {
        try {
            const { data } = await API.get(
                `/settings/quote-product-type?settingId=${settingId}`
            )
            return data.productTypes || []
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createQuoteProductType(params) {
        try {
            const { data } = await API.post(`/settings/quote-product-type`, {
                settingId: params.settingId,
                name: params.name,
                description: params.description,
            })
            return data.productType
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateQuoteProductType(params) {
        try {
            const { data } = await API.put(
                `/settings/quote-product-type/${params.settingId}`,
                {
                    paramsToUpdate: {
                        id: params.itemId,
                        ...params.paramsToUpdate,
                    },
                }
            )
            return data.productType
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteQuoteProductType(params) {
        try {
            const response = await API.delete(
                `/settings/quote-product-type?settingId=${params.settingId}&id=${params.itemId}`
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getQuoteRanges(idSetting) {
        try {
            const response = await API.get(
                `/settings/quote/quotes-ranges/get-ranges?idSetting=${idSetting}`
            )
            return response.data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateQuoteRanges(idSetting, range) {
        try {
            const response = await API.post(
                '/settings/quote/quotes-ranges/add-range',
                { idSetting, range }
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getQuoteExpirationDays(idSetting) {
        try {
            const response = await API.get(
                `/settings/quote/expiration/get-expiration-days?idSetting=${idSetting}`
            )
            return response.data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updateQuoteExpirationDays(idSetting, quoteExpirationDays) {
        try {
            const response = await API.post(
                '/settings/quote/expiration/update-expiration-days',
                { idSetting, quoteExpirationDays }
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    //  Milestones
    async getMilestones(settingId) {
        try {
            const {
                data: { milestones },
            } = await API.get(`/settings/milestone/?settingId=${settingId}`)
            return milestones
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getMilestone(settingId, milestoneId) {
        try {
            const {
                data: { milestone },
            } = await API.get(
                `/settings/milestone/?settingId=${settingId}&milestoneId=${milestoneId}`
            )
            return milestone
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createMilestone(params) {
        try {
            const {
                data: { milestone },
            } = await API.post(`/settings/milestone/`, params)
            return milestone
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateMilestone(params) {
        try {
            const { milestoneId, ...paramsToUpdate } = params
            const {
                data: { milestone },
            } = await API.put(
                `/settings/milestone/${milestoneId}`,
                paramsToUpdate
            )
            return milestone
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteMilestone(params) {
        try {
            const {
                data: { message },
            } = await API.delete(
                `/settings/milestone/?settingId=${params.settingId}&milestoneId=${params.milestoneId}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateMilestonesOrder(settingId, milestones) {
        try {
            await API.put('/settings/milestone/update-order', {
                settingId,
                milestones,
            })
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    // cost milestones
    async updateCostMilestone(params) {
        try {
            const { quoteId, costId, ...paramsToUpdate } = params
            const {
                data: { milestones },
            } = await API.put(
                `/cost/milestone?quoteId=${quoteId}&costId=${costId}`,
                paramsToUpdate
            )
            return milestones
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async clearCostMilestones({ quoteId, costId }) {
        try {
            const {
                data: { milestones },
            } = await API.put(
                `/cost/milestone/clear?quoteId=${quoteId}&costId=${costId}`
            )
            return milestones
        } catch (error) {
            const errorResponse = error.response.data
            throw new Error(errorResponse.message || errorResponse.error)
        }
    },
    async getProjectsBySapCode({ codes }) {
        try {
            const {
                data: { projects },
            } = await API.get(
                `/projects/get-by-sapcode?codes=${encodeURIComponent(
                    JSON.stringify(codes)
                )}`
            )
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createPOC(params) {
        try {
            const {
                data: { response },
            } = await API.post(`/poc`, params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async updatePOC(params) {
        try {
            const {
                data: { response },
            } = await API.post(`/poc/update`, params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getAllPOC() {
        try {
            const {
                data: { pocs },
            } = await API.get(`/poc`)
            return pocs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    //external project users
    async createExternalUser(params) {
        try {
            const {
                data: { externalUser },
            } = await API.post('projects/external-user', params)
            return externalUser
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createExternalUserSIA(params) {
        try {
            const {
                data: { externalUser },
            } = await API.post('external-user/external-user', params)
            return externalUser
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProjectsByPM() {
        try {
            const {
                data: { projectsPM },
            } = await API.get('projects/projects-pm')
            return projectsPM
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProjectsByQuote(quoteId) {
        try {
            const {
                data: { projects },
            } = await API.get(`projects/by-quote?quoteId=${quoteId}`)
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProjectsBySuperintendent() {
        try {
            const {
                data: { projects },
            } = await API.get('projects/projects-superintendent')
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getProjectsByPayrollManager() {
        try {
            const {
                data: { projects },
            } = await API.get('projects/projects-payroll-manager')
            return projects
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async disabledUserByEmail(email) {
        try {
            const {
                data: { user },
            } = await API.put('/authentication/disable-user', { email })
            return user
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateExternalUser(params) {
        try {
            const { projectId, userId, ...paramsToUpdate } = params
            const {
                data: { externalUser },
            } = await API.put(
                `/projects/external-user?projectId=${projectId}&userId=${userId}`,
                paramsToUpdate
            )
            return externalUser
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateGeneralExternalUser(params) {
        try {
            const { userId, ...paramsToUpdate } = params
            const {
                data: { externalUser },
            } = await API.put(
                `/external-user/external-user?userId=${userId}`,
                paramsToUpdate
            )
            return externalUser
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async setClockOut(params) {
        try {
            const {
                data: { timesheet },
            } = await API.put('/projects/set-timesheet-clockout', params)
            return timesheet
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async disableUser({ userId, enabled }) {
        try {
            const {
                data: { user },
            } = await API.put('/users/disable-user', { userId, enabled })
            return user
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteExternalUser(params) {
        try {
            const {
                data: { message },
            } = await API.delete(`/projects/external-user`, { params })
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteGeneralExternalUser(params) {
        try {
            const {
                data: { message },
            } = await API.delete(`/external-user/external-user`, { params })
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateNotificationTemplate(params) {
        try {
            const {
                data: { response },
            } = await API.put(`/settings/update-notification-template`, params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getExternalUsers(params) {
        try {
            const {
                data: { externalUsers },
            } = await API.get('/projects/external-user', { params })
            return externalUsers
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getAllExternalUsers(params) {
        try {
            const {
                data: { externalUsers },
            } = await API.get('/external-user/all-external-user', { params })
            return externalUsers
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendTimesheetEmail(params) {
        try {
            const {
                data: { message },
            } = await API.put(
                `/projects/external-user/timesheet-email?projectId=${params.projectId}&userId=${params.userId}`
            )
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProjectTimesheetLink(projectId) {
        try {
            const {
                data: { link },
            } = await API.get(`/projects/timesheet-link?projectId=${projectId}`)
            return link
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getExternalUserByEmail(params) {
        try {
            const {
                data: { externalUser },
            } = await API.get('/external-user/', { params })
            return externalUser
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validateHash(hash) {
        try {
            const params = { hash }
            const {
                data: { hashData },
            } = await API.get('/external-user/validate-hash', { params })
            return hashData
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validateExternalUserPassword(params) {
        try {
            const {
                data: { password },
            } = await API.get('/external-user/validate-password', { params })
            return password
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async saveWorkLog(params) {
        try {
            const {
                data: { workLog },
            } = await API.post('/external-user/work-log', params)
            return workLog
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    //project locations
    async createProjectLocation(params) {
        try {
            const {
                data: { projectLocation },
            } = await API.post('projects/location', params)
            return projectLocation
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateProjectLocation(params) {
        try {
            const { projectId, locationId, ...paramsToUpdate } = params
            const {
                data: { projectLocation },
            } = await API.put(
                `/projects/location?projectId=${projectId}&locationId=${locationId}`,
                paramsToUpdate
            )
            return projectLocation
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteProjectLocation(params) {
        try {
            const {
                data: { message },
            } = await API.delete(`/projects/location`, { params })
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProjectLocations(params) {
        try {
            const {
                data: { projectLocations },
            } = await API.get('/projects/location', { params })
            return projectLocations
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createProjectActivity(params) {
        try {
            const {
                data: { projectActivity },
            } = await API.post('projects/activity', params)
            return projectActivity
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateProjectActivity(params) {
        try {
            const { projectId, activityId, ...paramsToUpdate } = params
            const {
                data: { projectActivity },
            } = await API.put(
                `/projects/activity?projectId=${projectId}&activityId=${activityId}`,
                paramsToUpdate
            )
            return projectActivity
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteProjectActivity(params) {
        try {
            const {
                data: { projectActivity },
            } = await API.delete(`/projects/activity`, { params })
            return projectActivity
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProjectActivities(params) {
        try {
            const {
                data: { projectActivities },
            } = await API.get('/projects/activity', { params })
            return projectActivities
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getAllProjectLocations(params) {
        try {
            const { initialDate, finishDate } = params
            let query = ''
            if (initialDate && finishDate) {
                query += `?initialDate=${initialDate}&finishDate=${finishDate}`
            }
            const {
                data: { projectLocations },
            } = await API.get(`/projects/all-locations${query}`)
            return projectLocations
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    //NOMINA

    async updateTimeSheet(params) {
        try {
            const response = await API.put('/projects/update-timesheet', params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async createTimesheet(params) {
        try {
            const {
                data: { timesheet },
            } = await API.post('/projects/create-timesheet', params)
            return timesheet
        } catch (error) {
            const errorResponse = error.response?.data || {}
            const err = new Error(
                errorResponse.message || errorResponse.error || error.message
            )
            err.response = error.response
            throw err
        }
    },

    async createTimesheetsBatch(params) {
        try {
            const { data } = await API.post(
                '/projects/create-timesheets-batch',
                params
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async assignPayrollProcess(params) {
        try {
            const response = await API.put(
                '/projects/update-timesheet-payroll',
                params
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async reportPayroll(params) {
        try {
            const response = await API.put('/projects/report-payroll', params)
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteAttachmentPayroll(params) {
        try {
            const response = await API.put(
                '/projects/delete-attachment-payroll',
                params
            )
            return response
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getPayrolls(params) {
        try {
            const {
                data: { payrolls },
            } = await API.get('/projects/payrolls', { params })
            return payrolls
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async deleteTimeSheet(params) {
        try {
            const {
                data: { message },
            } = await API.delete('/projects/delete-timesheet', { params })
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    //DAILY REPORTS
    async createReport(params) {
        try {
            const {
                data: { report },
            } = await API.post('projects/report', params)
            return report
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getDailyReports(params) {
        try {
            const {
                data: { reports },
            } = await API.get('/projects/daily-report', { params })
            return reports
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getWeeklyReports(params) {
        try {
            const {
                data: { reports },
            } = await API.get('/projects/weekly-report', { params })
            return reports
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async sendReportEmail(params) {
        try {
            const {
                data: { report },
            } = await API.post('projects/send-report', params)
            return report
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async sendPayrollEmail(params) {
        try {
            const {
                data: { report },
            } = await API.post('projects/send-payroll', params)
            return report
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async sendLinkToCreateReport(params) {
        try {
            const {
                data: { message },
            } = await API.put('projects/report/send-link', params)
            return message
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createReportFromLink(params) {
        try {
            const {
                data: { report },
            } = await API.post('external-user/create-report-from-link', params)
            return report
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateReprocessWorkOrder(params) {
        try {
            const {
                data: { order },
            } = await API.put('/workOrder/update-reprocess-work-order', params)
            return order
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getReprocessAvailableQty(workOrderId, woCode) {
        try {
            const {
                data: { availableQty },
            } = await API.get(
                `/workOrder/reprocess-available-qty?workOrderId=${workOrderId}&woCode=${woCode}`
            )
            return availableQty
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getDeletedWorkOrdersIds() {
        try {
            const {
                data: { workOrdersIds },
            } = await API.get(`/workOrder/deleted-wo-ids`)
            return workOrdersIds
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getClasificationsPerWorkOrder({ codes }) {
        try {
            const {
                data: { clasificationsPerWorkOrder },
            } = await API.post('/workOrder/clasifications-per-work-order', {
                codes,
            })
            return clasificationsPerWorkOrder
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getProjectWorkOrderCodes(projectId, companyId) {
        try {
            let queryParams = `projectId=${projectId}`
            if (companyId) {
                queryParams += `&companyId=${companyId}`
            }
            const {
                data: { workOrdersCodes },
            } = await API.get(`/workOrder/project-wo-codes?${queryParams}`)
            return workOrdersCodes
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getDeletedQuotesIds() {
        try {
            const {
                data: { quotesIds },
            } = await API.get(`/quotes/deleted-quotes-ids`)
            return quotesIds
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validateWOProduction(workOrderId) {
        try {
            const response = await API.get(
                `/workOrder/validate-production?workOrderId=${workOrderId}`
            )
            return response.data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validateWOQuality(workOrderId) {
        try {
            const response = await API.get(
                `/workOrder/validate-quality?workOrderId=${workOrderId}`
            )
            return response.data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async validateWOPackingLists(workOrderId) {
        try {
            const response = await API.get(
                `/packingList/validate-wo-packing-lists?workOrderId=${workOrderId}`
            )
            return response.data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getTimeRecords(params) {
        try {
            const {
                data: { timeRecords },
            } = await API.get('/projects/external-user/time-records', {
                params,
            })
            return timeRecords
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    //projects settings
    async createForecastInform(params) {
        try {
            const {
                data: { inform },
            } = await API.post('/settings/project/forecast-inform', params)
            return inform
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getForecastInformDetails(settingId) {
        try {
            const {
                data: { informs },
            } = await API.get(
                `/settings/project/forecast-inform-details?id=${settingId}`
            )
            return informs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateForecastInform(params) {
        try {
            const {
                data: { inform },
            } = await API.put('/settings/project/forecast-inform', params)
            return inform
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async selectScope(params) {
        try {
            const {
                data: { scope },
            } = await API.put(`cost/select-scope`, params)
            return scope
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async findByNumber(params) {
        try {
            const {
                data: { purchase },
            } = await API.get(`/purchase/findByNumber?number=${params.number}`)
            return purchase
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateAllCosts(params) {
        try {
            const {
                data: { costs },
            } = await API.put(`/cost/all`, params)
            return costs
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateCommissions(quoteId, params) {
        try {
            const {
                data: { quote, costs, alternatives },
            } = await API.put(`/quotes/${quoteId}/commissions`, {
                params,
            })
            return { quote, costs, alternatives }
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async uploadNoOrderFile(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/uploadNoOrderFile`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async createNoOrderPurchase(params) {
        try {
            const { data: result } = await API.post(
                `/purchase/create-no-order-purchase`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateNoOrderPurchase(params) {
        try {
            const { data: result } = await API.put(
                `/purchase/update-no-order-purchase`,
                params
            )
            return result
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },

    async getAccountantPurchases() {
        try {
            const {
                data: { purchases },
            } = await API.get(`/purchase/accountant-purchases`)
            return purchases
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    // DELETE UPDATE
    // async migratePurchaseDates() {
    //     try {
    //         const { data: result } = await API.post(`/purchase/migrate-dates`)
    //         return result
    //     } catch (error) {
    //         const errorResponse = error.response?.data || {}
    //         throw new Error(errorResponse.message || errorResponse.error || error.message)
    //     }
    // },

    // OPERATIONS REPORTS
    async getEnteredProducedAndAccumulatedPerProcessReport(params) {
        try {
            const {
                data: { report },
            } = await API.get(
                `/operations-reports/entered-produced-and-accumulated-per-process`,
                { params }
            )
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },
    async getEnteredProducedAndAccumulatedPerAllProcessesReport(params) {
        try {
            const {
                data: { report },
            } = await API.get(
                `/operations-reports/entered-produced-and-accumulated-per-all-processes`,
                { params }
            )
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async getRejectionReport(params) {
        try {
            const {
                data: { report },
            } = await API.get(`/operations-reports/rejection-report`, {
                params,
            })
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async getProducedKgPerProcessReport(params) {
        try {
            const {
                data: { report },
            } = await API.get(
                `/operations-reports/produced-kg-per-process-report`,
                {
                    params,
                }
            )
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async getProductTypeReport(params) {
        try {
            const {
                data: { report },
            } = await API.get(`/operations-reports/product-type-report`, {
                params,
            })
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async getProductReport(params) {
        try {
            const {
                data: { report },
            } = await API.get(`/operations-reports/product-report`, {
                params,
            })
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async getPerProcessReport(params) {
        try {
            const {
                data: { report },
            } = await API.get(`/operations-reports/per-process-report`, {
                params,
            })
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async getShippedPerMonthReport(params) {
        try {
            const {
                data: { report },
            } = await API.get(`/operations-reports/shipped-per-month-report`, {
                params,
            })
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async generateRejectedWorkOrdersReport(params) {
        try {
            const {
                data: { report },
            } = await API.get(
                `/operations-reports/generated-rejected-wo-report`,
                {
                    params,
                }
            )
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    async generateHistoryOfGeneratedWO(params) {
        try {
            const {
                data: { report },
            } = await API.get(
                `/operations-reports/history-of-generated-wo-report`,
                {
                    params,
                }
            )
            return report
        } catch (error) {
            throw new Error(buildReportErrorMessage(error))
        }
    },

    /**
     *
     * ALERTS ENDPOINTS
     *
     */
    async createAlert(params) {
        try {
            const {
                data: { alert },
            } = await API.post(`/alert`, params)
            return alert
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateAlert(workOrderId, alertId, params) {
        try {
            const {
                data: { alert },
            } = await API.put(`/alert/${workOrderId}/${alertId}`, params)
            return alert
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getAlertsByWorkOrder(workOrderId) {
        try {
            const {
                data: { alerts },
            } = await API.get(`/alert/work-order/${workOrderId}`)
            return alerts
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getAlertSummary(workOrderId) {
        try {
            const {
                data: { summary },
            } = await API.get(`/alert/work-order/${workOrderId}/summary`)
            return summary
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getAlert(workOrderId, alertId) {
        try {
            const {
                data: { alert },
            } = await API.get(`/alert/${workOrderId}/${alertId}`)
            return alert
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async supervisorReviewAlert(workOrderId, params) {
        try {
            const {
                data: { alert },
            } = await API.put(`/alert/${workOrderId}/supervisor-review`, params)
            return alert
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async makeAlertChanges(workOrderId, params) {
        try {
            const {
                data: { alert },
            } = await API.put(`/alert/${workOrderId}/make-changes`, params)
            return alert
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async supervisorApproveAlert(workOrderId, params) {
        try {
            const {
                data: { alert },
            } = await API.put(
                `/alert/${workOrderId}/supervisor-approve`,
                params
            )
            return alert
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async applyAlertChanges(workOrderId, params) {
        try {
            const { data } = await API.put(
                `/alert/${workOrderId}/apply-changes`,
                params
            )
            return data
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getAlertsByStatus(status, role) {
        try {
            const {
                data: { alerts },
            } = await API.get(`/alert/status/${status}`, {
                params: { role },
            })
            return alerts
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getAlertsSummaries(workOrderIds) {
        try {
            const {
                data: { summaries },
            } = await API.post(`/alert/summaries`, {
                workOrderIds,
            })
            return summaries
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async getUserAlerts() {
        try {
            const {
                data: { alerts },
            } = await API.get(`/alert/my-alerts`)
            return alerts
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async deleteAlert(workOrderId, alertId) {
        try {
            await API.delete(`/alert/${workOrderId}/${alertId}`)
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
    async updateClientQuote(quoteId, params) {
        try {
            const {
                data: { quote },
            } = await API.put(`/quotes/clientId/${quoteId}`, params)
            return quote
        } catch (error) {
            const errorResponse = error.response?.data || {}
            throw new Error(
                errorResponse.message || errorResponse.error || error.message
            )
        }
    },
}
